import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Encrypt } from "../../utils/encryption";
import { getHubPlanId } from "../../utils/subscriptionState";
import { userRegistration, handleMedia } from "../../config/api";
import { OPERATION_TYPE } from "../../config/mediaOperationTypes";
import { BIZ } from "../../config/zinihubConfig";
import { ADDRESS_LINE_MAX } from "../../config/fieldLimits";
import StorefrontPreview from "./StorefrontPreview";
import BizImageUpload from "./BizImageUpload";
import { BUSINESS_DOCS_CONFIG } from "../../config/businessDocsConfig";
import { BUSINESS_DOCS_LABELS } from "../../labels/businessDocsLabels";
import { CATALOG_LIMIT_LABELS } from "../../labels/catalogLimitLabels";
import "../../styles/Register.css";
import ProductBuilder from "../ProductBuilder/ProductBuilder";
import storeCategories from "./StoreCategoryList.json";
import { hasServiceTemplate, getServiceTemplate } from "./serviceCatalogTemplates";
import { hasProductTemplate, getProductTemplate } from "./productCatalogTemplates";
import { getCategoryLabel } from "./categoryIcons";
import CatalogImport from "./CatalogImport";
import dayjs from "dayjs";
import { compareStorePasskey, getBizPasskeyHashFromSettings } from "../../utils/bizPasskeyHash";
import {
  itemsGalleryLinkageComparableKey,
  syncItemsGalleryLinkedItemIds,
  invalidateManifestFetchCacheForPath,
  fetchManifestForPath,
  putManifestWithPresignedUrl,
} from "../../utils/mediaManifest";
import { putPresignedObject } from "../../utils/putPresignedObject";
import { handleAddOrEditShop, GenerateShortID } from "./StoreUtils";
import { HUB_ITEM } from "./hubItemFields";
import { CDN_IMAGE_BASE, cdnUrl, cdnUrlForStorageKey, mediaPath, ITEMS_SHARED_GALLERY_ITEM_ID, cdnSafeMediaPathSegment, basenameFromStorageKey, getDeployEnv } from "../../config/mediaPaths";
import {
  countryCodes,
  countryOptionsForAddress,
  getPhoneCodeForCountry,
  PrefixFormItem,
  mobileForApi,
  isMobileLengthValid,
  MOBILE_MAX_LEN,
  userNameDisplayFromPrefix,
  getDefaultPhoneFromDecodedToken,
  parseApiMobileStringToPrefixAndDigits,
} from "../../constants/CountryCodes";
import styles from "./StoreForm.module.css";
import SelectCard from "../../components/SelectCard/SelectCard";
import ActionGroup from "../../components/common/ActionGroup/ActionGroup";
import { UploadOutlined, PlusOutlined, GlobalOutlined, LeftOutlined, RightOutlined, CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import LocationPicker from "../../components/LocationPicker";
import {
  buildMetadataSnapshot,
  mergeImplicitDeletionsFromStoreTypeChange,
  computeUpdateTarget,
  sortedItemsComparableKey,
  selectBizEditBaseline,
} from "./bizEditSelectors";
import { setBizEditBaseline, clearBizEditBaseline, setCatalogBuilderDirty, setCatalogProducts, setCatalogServices } from "../../store/bizEditSlice";
import { setGalleryManifestForPath } from "../../store/gallerySlice";
import {
  App as AntApp,
  Form,
  Input,
  Button,
  Select,
  Modal,
  Space,
  Tooltip,
  Calendar,
  Checkbox,
  Badge,
  Tag,
  Popover,
  InputNumber,
} from "antd";

const COUNTRY_FLAGS = {
  "India":"🇮🇳","United Arab Emirates":"🇦🇪","United States":"🇺🇸","United Kingdom":"🇬🇧",
  "Canada":"🇨🇦","Australia":"🇦🇺","Singapore":"🇸🇬","Malaysia":"🇲🇾",
  "Saudi Arabia":"🇸🇦","Qatar":"🇶🇦","Kuwait":"🇰🇼","Oman":"🇴🇲","Bahrain":"🇧🇭",
  "Germany":"🇩🇪","France":"🇫🇷","Italy":"🇮🇹","Spain":"🇪🇸","Netherlands":"🇳🇱",
  "Switzerland":"🇨🇭","Sweden":"🇸🇪","Norway":"🇳🇴","Denmark":"🇩🇰","Finland":"🇫🇮",
  "Japan":"🇯🇵","South Korea":"🇰🇷","China":"🇨🇳","Russia":"🇷🇺","Brazil":"🇧🇷",
  "Mexico":"🇲🇽","South Africa":"🇿🇦","New Zealand":"🇳🇿","Indonesia":"🇮🇩",
  "Turkey":"🇹🇷","Thailand":"🇹🇭","Vietnam":"🇻🇳","Philippines":"🇵🇭",
  "Pakistan":"🇵🇰","Bangladesh":"🇧🇩","Sri Lanka":"🇱🇰","Nepal":"🇳🇵",
  "Ireland":"🇮🇪","Austria":"🇦🇹","Belgium":"🇧🇪","Greece":"🇬🇷",
  "Portugal":"🇵🇹","Poland":"🇵🇱","Czech Republic":"🇨🇿","Hungary":"🇭🇺",
  "Romania":"🇷🇴","Ukraine":"🇺🇦","Israel":"🇮🇱","Egypt":"🇪🇬",
  "Nigeria":"🇳🇬","Kenya":"🇰🇪","Morocco":"🇲🇦","Argentina":"🇦🇷",
  "Chile":"🇨🇱","Colombia":"🇨🇴","Peru":"🇵🇪","Venezuela":"🇻🇪",
};

function normalizeGeocode(raw) {
  if (!raw) return null;
  let value = raw;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      value = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object") return null;
  const lat = Number(value.lat);
  const lon = Number(value.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return {
    lat: Number(lat.toFixed(6)),
    lon: Number(lon.toFixed(6)),
  };
}

function geocodeFormValue(raw) {
  const normalized = normalizeGeocode(raw);
  return normalized ? JSON.stringify(normalized) : "";
}

function normalizePaymentMethods(raw, { kycVerified } = {}) {
  const source = Array.isArray(raw) ? raw : ["SELF_MANAGED"];
  const allowed = new Set(["SELF_MANAGED", ...(kycVerified ? ["GATEWAY"] : [])]);
  const methods = source.filter((method) => allowed.has(method));
  return methods.length ? methods : ["SELF_MANAGED"];
}
const getFlagEmoji = (countryName) => COUNTRY_FLAGS[countryName] || "🌍";

const SOCIAL_FOOTPRINT_KEYS = ["youtube", "instagram", "facebook", "linkedin", "x"];

function normalizeSocialFootprints(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const normalized = {};
  for (const key of SOCIAL_FOOTPRINT_KEYS) {
    const trimmed = String(source[key] || "").trim();
    if (trimmed) normalized[key] = trimmed;
  }
  return normalized;
}

const { Option } = Select;

const OverflowTooltip = ({ title, children }) => {
  const ref = React.useRef(null);
  const [overflow, setOverflow] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (el) setOverflow(el.scrollWidth > el.clientWidth);
  }, [title]);
  return overflow ? <Tooltip title={title}><span ref={ref}>{children}</span></Tooltip> : <span ref={ref}>{children}</span>;
};

const MonthPickerPanel = ({ datePickerMonth, setDatePickerMonth, group, gi, prebookingGroups, setPrebookingGroups, form, dateGroupMap, GROUP_COLORS }) => {
  const today = dayjs().startOf("day");
  const startOfMonth = datePickerMonth.startOf("month");
  const startDow = startOfMonth.day();
  const daysInMonth = datePickerMonth.daysInMonth();
  const rows = [];
  let cells = [];
  const doUpdate = (updated) => {
    setPrebookingGroups(updated);
    form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
  };
  for (let i = 0; i < startDow; i++) cells.push(<div key={`blank-${i}`} />);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = startOfMonth.date(d);
    const key = date.format("YYYY-MM-DD");
    const isPast = date.isBefore(today);
    const inGroup = group.datePickerValue.some(dd => dd.format("YYYY-MM-DD") === key);
    const otherGis = dateGroupMap[key] || [];
    const groupColor = GROUP_COLORS[gi % GROUP_COLORS.length];
    cells.push(
      <div key={key} style={{ textAlign: "center", cursor: isPast ? "default" : "pointer", padding: 2, display: "flex", alignItems: "center", justifyContent: "center", opacity: isPast ? 0.35 : 1 }}
        onClick={() => {
          if (isPast) return;
          const updated = prebookingGroups.map(g => {
            if (g.key !== group.key) return g;
            const exists = g.datePickerValue.some(d => d.format("YYYY-MM-DD") === key);
            const newDates = exists
              ? g.datePickerValue.filter(d => d.format("YYYY-MM-DD") !== key)
              : [...g.datePickerValue, date];
            return { ...g, datePickerValue: newDates };
          });
          doUpdate(updated);
        }}
      >
        <div style={{ width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
          background: inGroup ? groupColor : "transparent", color: inGroup ? "#fff" : undefined,
          fontSize: 13, fontWeight: inGroup ? 600 : 400, position: "relative", textDecoration: isPast ? "line-through" : "none" }}>
          {d}
          {otherGis.filter(ogi => ogi !== gi).map(ogi => (
            <div key={ogi} style={{ width: 4, height: 4, borderRadius: "50%", display: "inline-block", margin: "0 1px", background: GROUP_COLORS[ogi % GROUP_COLORS.length] }} />
          ))}
        </div>
      </div>
    );
    if (cells.length === 7) {
      rows.push(<div key={`row-${rows.length}`} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>{cells}</div>);
      cells = [];
    }
  }
  if (cells.length) {
    while (cells.length < 7) cells.push(<div key={`blank-end-${cells.length}`} />);
    rows.push(<div key={`row-${rows.length}`} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>{cells}</div>);
  }
  return (
    <div style={{ width: 280 }}>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "0 0 8px" }}>
        <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => setDatePickerMonth(datePickerMonth.subtract(1, "month"))} />
        <span style={{ fontWeight: 600, fontSize: 14, minWidth: 120, textAlign: "center" }}>{datePickerMonth.format("MMMM YYYY")}</span>
        <Button type="text" size="small" icon={<RightOutlined />} onClick={() => setDatePickerMonth(datePickerMonth.add(1, "month"))} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", fontSize: 11, color: "#888", marginBottom: 4 }}>
        <div style={{ textAlign: "center" }}>S</div><div style={{ textAlign: "center" }}>M</div><div style={{ textAlign: "center" }}>T</div><div style={{ textAlign: "center" }}>W</div><div style={{ textAlign: "center" }}>T</div><div style={{ textAlign: "center" }}>F</div><div style={{ textAlign: "center" }}>S</div>
      </div>
      {rows}
    </div>
  );
};

const CreateStoreWithPreview = () => {
  const { message } = AntApp.useApp();
  const { token, decodeToken, subscriptionCatalog } = useAuth();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    mode,
    shop: initialValues,
    existingBizIds,
    expandSettings,
    privateEditUnlockedFromHub,
  } = location.state || {};
  /** Biz ids already used in this hub — stable array ref for hooks / onFinish. */
  const allStores = useMemo(
    () => (Array.isArray(existingBizIds) ? existingBizIds : []),
    [existingBizIds]
  );

  const dispatch = useDispatch();
  const baseline = useSelector(selectBizEditBaseline);

  const [step, setStep] = React.useState(1);

  const formRef = React.useRef(null);

  const scrollToTop = () => {
    if (formRef.current) {
      let el = formRef.current.parentElement;
      while (el) {
        if (getComputedStyle(el).overflowY === "auto" || getComputedStyle(el).overflowY === "scroll") {
          el.scrollTop = 0; return;
        }
        el = el.parentElement;
      }
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  };
  const TOT_STEPS = 3;
  const [, setActiveCollapseKey] = React.useState(["details"]);
  const [loading, setLoading] = React.useState(false);
  const [deletedItemIds, setDeletedItemIds] = React.useState([]);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [passkeyPromptOpen, setPasskeyPromptOpen] = React.useState(false);
  const [passkeyPromptValue, setPasskeyPromptValue] = React.useState("");
  const [privateEditUnlocked, setPrivateEditUnlocked] = React.useState(true);
  const [editUnlockModalOpen, setEditUnlockModalOpen] = React.useState(false);
  const [editUnlockPasskeyInput, setEditUnlockPasskeyInput] = React.useState("");
  const editUnlockPasskeyInputRef = React.useRef(null);
  const passkeyPromptInputRef = React.useRef(null);
  const [areaInput, setAreaInput] = React.useState("");
  const MAX_SERVICE_AREAS = 10;


  const [contactVerifyLoading, setContactVerifyLoading] = React.useState(false);
  const [contactResendLoading, setContactResendLoading] = React.useState(false);
  const [contactResendCountdown, setContactResendCountdown] = React.useState(0);
  const [contactShowOtpInput, setContactShowOtpInput] = React.useState(false);
  const [contactNumberVerified, setContactNumberVerified] = React.useState(false);
  const [contactOtp, setContactOtp] = React.useState("");
  const [contactVerificationToken, setContactVerificationToken] = React.useState(null);
  const [contactEditMode, setContactEditMode] = React.useState(false);
  const [itemsVersion, setItemsVersion] = React.useState(0);
  const [builderOpen, setBuilderOpen] = React.useState(false);
  const [productBuilderKey, setProductBuilderKey] = React.useState(0);
  const formDirtyRef = React.useRef(false);
  const pendingNavActionRef = React.useRef(null);
  const [navigationConfirmOpen, setNavigationConfirmOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [builderSaving, setBuilderSaving] = React.useState(false);
  const catalogBuilderDirty = useSelector((state) => state.bizEdit.catalogBuilderDirty);
  const bizEditBaseline = useSelector((state) => state.bizEdit.baseline);
  const catalogProducts = useSelector(state => state.bizEdit.products);
  const catalogServices = useSelector(state => state.bizEdit.services);
  const catalogHasUnsavedChanges = useMemo(() => {
    if (catalogBuilderDirty) return true;
    if (!bizEditBaseline) return false;
    return (
      sortedItemsComparableKey(catalogProducts) !== bizEditBaseline.productsKey ||
      sortedItemsComparableKey(catalogServices) !== bizEditBaseline.servicesKey
    );
  }, [catalogBuilderDirty, bizEditBaseline, catalogProducts, catalogServices]);
  const [editingProductIdx, setEditingProductIdx] = React.useState(null);
  const [editingProductData, setEditingProductData] = React.useState(null);
  const editingIdxRef = React.useRef(null);
  const productBuilderRef = React.useRef(null);
  const catalogProductsRef = React.useRef([]);
  const catalogServicesRef = React.useRef([]);
  const [createdBizId, setCreatedBizId] = React.useState(null);
  const [createdHubId, setCreatedHubId] = React.useState(null);
  const [step2Loading, setStep2Loading] = React.useState(false);
  const step2SnapshotRef = React.useRef(null);
  const bizVersionRef = React.useRef(null);
  const [availableBusinessDocs, setAvailableBusinessDocs] = React.useState([]);
  const [zipLookupLoading, setZipLookupLoading] = React.useState(false);
  const [zipLookupFailed, setZipLookupFailed] = React.useState(false);
  const [templateApplied, setTemplateApplied] = useState(null); // { category: string, type: "product"|"service" } or null
  const [prebookingGroups, setPrebookingGroups] = useState([]);
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  const [datePopoverKey, setDatePopoverKey] = useState(null);
  const [datePickerMonth, setDatePickerMonth] = useState(dayjs().startOf("month"));
  const [slotPopoverKey, setSlotPopoverKey] = useState(null);
  const [pendingSlots, setPendingSlots] = useState([]);

  const GROUP_COLORS = ["#52c41a", "#1890ff", "#faad14", "#f5222d", "#722ed1", "#13c2c2", "#eb2f96", "#fa8c16"];
  const DURATION_OPTIONS = [
    { value: 30, label: "30 min" },
    { value: 60, label: "1 hr" },
    { value: 90, label: "1.5 hr" },
    { value: 120, label: "2 hr" },
    { value: 150, label: "2.5 hr" },
    { value: 180, label: "3 hr" },
  ];
  const BREAK_DURATION_OPTIONS = [
    { value: 0, label: "None" },
    { value: 5, label: "5 min" },
    { value: 10, label: "10 min" },
    { value: 15, label: "15 min" },
    { value: 30, label: "30 min" },
  ];

  const generateTimeSlots = useCallback((durationMin, breakMin = 0) => {
    const slots = [];
    const step = durationMin + breakMin;
    for (let min = 0; min + durationMin <= 24 * 60; min += step) {
      const sh = Math.floor(min / 60);
      const sm = min % 60;
      const eh = Math.floor((min + durationMin) / 60);
      const em = (min + durationMin) % 60;
      slots.push({
        start: `${String(sh).padStart(2, "0")}:${String(sm).padStart(2, "0")}`,
        end: `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`,
      });
    }
    return slots;
  }, []);

  const slotDisplayValue = useCallback((slot) => {
    if (!slot || typeof slot !== "object") return "";
    const start = typeof slot.start === "string" ? slot.start.trim() : "";
    const end = typeof slot.end === "string" ? slot.end.trim() : "";
    if (start && end) return `${start} — ${end}`;
    return "";
  }, []);

  const dateGroupMap = useMemo(() => {
    const map = {};
    prebookingGroups.forEach((group, gi) => {
      (group.datePickerValue || []).forEach(date => {
        if (date && date.format) {
          const key = date.format("YYYY-MM-DD");
          map[key] = [...(map[key] || []), gi];
        }
      });
    });
    return map;
  }, [prebookingGroups]);

  useEffect(() => {
    catalogProductsRef.current = catalogProducts;
  }, [catalogProducts]);
  useEffect(() => {
    catalogServicesRef.current = catalogServices;
  }, [catalogServices]);

  const hasCatalogItems = () =>
    catalogProductsRef.current.length > 0 || catalogServicesRef.current.length > 0;

  const markBizLive = () => {
    if (!hasCatalogItems()) {
      message.warning("Add at least one product or service to the catalogue before marking the store Live.");
      return;
    }
    form.setFieldsValue({ bizLive: true });
  };

  useEffect(() => {
    form.setFieldsValue({ products: catalogProducts, services: catalogServices });
  }, [catalogProducts, catalogServices, form]);

  const handleCatalogDirtyChange = React.useCallback((isDirty) => {
    dispatch(setCatalogBuilderDirty(isDirty));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearBizEditBaseline());
    };
  }, [dispatch]);

  useEffect(() => {
    // Hub list already verified passkey before navigate (Stores handleProtectedAction).
    if (privateEditUnlockedFromHub) {
      setPrivateEditUnlocked(true);
      return;
    }
    if (
      mode === "edit" &&
      initialValues?.BizSettings?.bizPrivate &&
      getBizPasskeyHashFromSettings(initialValues.BizSettings)
    ) {
      setPrivateEditUnlocked(false);
    } else {
      setPrivateEditUnlocked(true);
    }
  }, [mode, initialValues, privateEditUnlockedFromHub]);

  // Snapshot Step 2 values on mount for change detection (edit mode)
  useEffect(() => {
    if (mode === "edit" && initialValues) {
      bizVersionRef.current = initialValues?.version ?? initialValues?.Version ?? 1;
      const phoneDisplay = userNameDisplayFromPrefix(
        parseApiMobileStringToPrefixAndDigits(initialValues.phone || "").prefix,
        parseApiMobileStringToPrefixAndDigits(initialValues.phone || "").digits
      );
      step2SnapshotRef.current = {
        name: initialValues.name || "",
        storeType: Array.isArray(initialValues.storeType) ? [...initialValues.storeType].sort().join(",") : initialValues.storeType || "",
        address: initialValues.address || "",
        locality: initialValues.locality || "",
        city: initialValues.city || "",
        state: initialValues.state || "",
        country: initialValues.country || "",
        zip: initialValues.zip || "",
        mapUrl: initialValues.mapUrl || "",
        geocode: geocodeFormValue(initialValues.geocode),
        phone: phoneDisplay,
        productSegment: initialValues.productSegment || "",
        serviceSegment: initialValues.serviceSegment || "",
        bizPrivate: !!initialValues.BizSettings?.bizPrivate,
        bizImageKey: initialValues.bizImageKey || "",
        BizSettings: {},
      };
    }
  }, [mode, initialValues]);

  // Resolve the selected hub and its catalog-backed private-store entitlement.
  const decodedToken = React.useMemo(() => {
    if (token) {
      return decodeToken(token);
    }
    return null;
  }, [token, decodeToken]);

  /** API-shaped mobile (isd-digits) for comparing “unchanged” vs logged-in / saved Biz phone. */
  const contactNumberBaselineApi = useMemo(() => {
    if (mode === "edit" && initialValues?.phone) {
      const parsed = parseApiMobileStringToPrefixAndDigits(initialValues.phone);
      return parsed ? mobileForApi(parsed.prefix, parsed.digits) : null;
    }
    const fromProfile = getDefaultPhoneFromDecodedToken(decodedToken);
    return fromProfile ? mobileForApi(fromProfile.prefix, fromProfile.digits) : null;
  }, [mode, initialValues?.phone, decodedToken]);

  // Hub profiles: same approach as Stores.js (MyHubAdmin + StaffAssignments, dedupe by PK)
  const hubProfiles = React.useMemo(() => {
    const profiles = [];
    const addProfile = (profile) => {
      if (!profile) return;
      profiles.push(profile);
    };
    const myHubAdmin = decodedToken?.MyHubAdmin;
    if (Array.isArray(myHubAdmin) && myHubAdmin.length > 1) {
      const hubProfile = myHubAdmin[1]?.HubProfile;
      if (Array.isArray(hubProfile)) {
        hubProfile.forEach(addProfile);
      } else {
        addProfile(hubProfile);
      }
    }
    if (Array.isArray(decodedToken?.StaffAssignments)) {
      decodedToken.StaffAssignments.forEach((assignment) => {
        addProfile(assignment?.HubProfile);
      });
    }
    const uniqueByPk = new Map();
    profiles.forEach((profile) => {
      const pk = profile?.PK;
      if (pk && !uniqueByPk.has(pk)) uniqueByPk.set(pk, profile);
    });
    return Array.from(uniqueByPk.values());
  }, [decodedToken]);

  // Resolve hub for this form: from nav state (create), or store's hubId (edit), or first profile
  const selectedHubIdForForm = React.useMemo(
    () =>
      location.state?.selectedHubId ??
      initialValues?.hubId ??
      hubProfiles[0]?.PK ??
      "",
    [location.state?.selectedHubId, initialValues?.hubId, hubProfiles]
  );

  const selectedHubProfile = React.useMemo(
    () => hubProfiles.find((p) => p?.PK === selectedHubIdForForm) || null,
    [hubProfiles, selectedHubIdForForm]
  );

  React.useEffect(() => {
    const loadBusinessDocs = async () => {
      if (!selectedHubProfile?.PK) {
        setAvailableBusinessDocs([]);
        return;
      }
      try {
        const manifestKey = `MyHub/${getDeployEnv()}/public/hub/${cdnSafeMediaPathSegment(selectedHubProfile.PK)}/docs/manifest.json`;
        const response = await fetch(cdnUrlForStorageKey(manifestKey), { cache: "no-store" });
        if (!response.ok) {
          setAvailableBusinessDocs([]);
          return;
        }
        const parsed = await response.json();
        setAvailableBusinessDocs(Array.isArray(parsed?.docs) ? parsed.docs : []);
      } catch (error) {
        console.warn("load business docs manifest", error);
        setAvailableBusinessDocs([]);
      }
    };
    loadBusinessDocs();
  }, [selectedHubProfile?.PK]);

  // Subscription tier for the selected hub (Plan is on HubProfile for both MyHubAdmin and StaffAssignments)
  /**
   * Resolved hub `Plan` only after we know which HubProfile row applies.
   * `null` = still loading / unknown — do not assume a plan here, or private Biz
   * is incorrectly forced public (hydrate + useEffect) before profiles arrive.
   */
  const subscriptionTier = React.useMemo(() => {
    if (!selectedHubProfile) return null;
    return getHubPlanId(selectedHubProfile);
  }, [selectedHubProfile]);
  const selectedCatalogPlan = React.useMemo(
    () => subscriptionCatalog?.plans?.find(
      (plan) => plan.enabled && plan.id === subscriptionTier
    ) || null,
    [subscriptionCatalog, subscriptionTier]
  );
  const productItemLimit = Number(selectedCatalogPlan?.limits?.productItems || 0);
  const serviceItemLimit = Number(selectedCatalogPlan?.limits?.serviceItems || 0);
  const privateStoreAllowed = selectedCatalogPlan
    ? selectedCatalogPlan.modules?.privateStore === true
    : null;

  const kycVerified = selectedHubProfile?.kycVerified === true;

  /** Plan limits remain unavailable until the selected catalog plan resolves. */
  const rawStoreType = Form.useWatch("storeType", form);
  const watchedValues = Form.useWatch([], form);
  /** Always an array — `.includes` on non-arrays/objects throws (e.g. truthy `{}` from bad state). */
  const visibleStoreType = useMemo(() => {
    const raw = rawStoreType ?? watchedValues?.storeType;
    if (Array.isArray(raw)) return raw;
    if (raw == null || raw === "") return [];
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawStoreType, watchedValues?.storeType, step, itemsVersion]);
  const watchedLocality = Form.useWatch("locality", form);
  const watchedCity = Form.useWatch("city", form);
  const watchedState = Form.useWatch("state", form);
  const watchedCountry = Form.useWatch("country", form);
  const watchedZip = Form.useWatch("zip", form);
  const watchedGeocode = Form.useWatch("geocode", form);
  const watchedPhonePrefix = Form.useWatch("phonePrefix", form);
  const watchedPhoneDigits = Form.useWatch("phoneDigits", form);
  const watchedBizPrivate = Form.useWatch("bizPrivate", form);
  const watchedBizOpenStatus = Form.useWatch("bizOpenStatus", form);
  const watchedBizLive = Form.useWatch("bizLive", form);
  const watchedFlashEnabled = Form.useWatch("flashEnabled", form);
  const watchedBusinessDocs = Form.useWatch("businessDocs", form) || [];
  const watchedDeliveryOptions = Form.useWatch("deliveryOptions", form) || ["PICKUP"];
  const watchedPaymentMethods = Form.useWatch("paymentMethods", form) || ["SELF_MANAGED"];
  const watchedServiceCoverageScope = Form.useWatch("serviceCoverageScope", form);
  const watchedServiceCoverageAreas = Form.useWatch("serviceCoverageAreas", form) || [];

  const countryNameToIsoCode = useMemo(() => {
    const entries = countryCodes
      .map((entry) => {
        const iso = String(entry.label || "").split(" ")[0]?.trim();
        if (!iso || iso.length !== 2) return null;
        return [entry.countryName, iso.toUpperCase()];
      })
      .filter(Boolean);
    return Object.fromEntries(entries);
  }, []);

  const sortedProductCategories = useMemo(() => {
    const categories = Array.isArray(storeCategories.productCategories)
      ? [...storeCategories.productCategories]
      : [];
    return categories.sort((a, b) => {
      const aPriority = hasProductTemplate(a.id) ? 0 : 1;
      const bPriority = hasProductTemplate(b.id) ? 0 : 1;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return String(a.name).localeCompare(String(b.name));
    });
  }, []);
  const sortedServiceCategories = useMemo(() => {
    const categories = Array.isArray(storeCategories.serviceCategories)
      ? [...storeCategories.serviceCategories]
      : [];
    return categories.sort((a, b) => {
      const aPriority = hasServiceTemplate(a.id) ? 0 : 1;
      const bPriority = hasServiceTemplate(b.id) ? 0 : 1;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return String(a.name).localeCompare(String(b.name));
    });
  }, []);
  const productCategoryOptions = useMemo(
    () =>
      sortedProductCategories.map((cat) => ({
        value: cat.id,
        label: cat.name,
      })),
    [sortedProductCategories]
  );
  const serviceCategoryOptions = useMemo(
    () =>
      sortedServiceCategories.map((cat) => ({
        value: cat.id,
        label: cat.name,
      })),
    [sortedServiceCategories]
  );
  const buildTypesenseExtras = useCallback((values) => {
    const productId = values?.productSegment || "";
    const serviceId = values?.serviceSegment || "";
    const productLabel = getCategoryLabel(productId) || undefined;
    const serviceLabel = getCategoryLabel(serviceId) || undefined;
    return {
      bizLive: values?.bizLive === true,
      categoryLabel: {
        ...(productLabel ? { product: productLabel } : {}),
        ...(serviceLabel ? { service: serviceLabel } : {}),
      },
      bizImageKey: values?.bizImageKey || "",
    };
  }, []);
  const filterCategoryOption = useCallback((input, option) => {
    const label = String(option?.label ?? option?.value ?? "").toLowerCase();
    return label.includes(String(input || "").trim().toLowerCase());
  }, []);

  /* previewBizIdForModal removed — inline preview overlay uses watchedValues directly */

  // Auto-populate step 3 services when a templated service category is selected in step 2
  const watchedServiceSegment = Form.useWatch("serviceSegment", form);
  useEffect(() => {
    if (!watchedServiceSegment) return;
    if (!hasServiceTemplate(watchedServiceSegment)) return;
    // Only in create mode, only when services list is still empty
    if (mode === "edit") return;
    const existingServices = catalogServicesRef.current;
    if (existingServices.length > 0) return;
    const tpl = getServiceTemplate(watchedServiceSegment);
    const limitedServices = tpl.suggestedServices.slice(0, serviceItemLimit);
    catalogServicesRef.current = limitedServices;
    form.setFieldsValue({ services: limitedServices });
    dispatch(setCatalogServices(limitedServices));
    dispatch(setCatalogBuilderDirty(true));
    setTemplateApplied({ category: watchedServiceSegment, type: "service" });
  }, [watchedServiceSegment, mode, form, dispatch, serviceItemLimit]);

  // Auto-populate step 3 products when a templated product category is selected in step 2
  const watchedProductSegment = Form.useWatch("productSegment", form);
  useEffect(() => {
    if (!watchedProductSegment) return;
    if (!hasProductTemplate(watchedProductSegment)) return;
    if (mode === "edit") return;
    const existingProducts = catalogProductsRef.current;
    if (existingProducts.length > 0) return;
    const tpl = getProductTemplate(watchedProductSegment);
    const limitedProducts = tpl.suggestedProducts.slice(0, productItemLimit);
    catalogProductsRef.current = limitedProducts;
    form.setFieldsValue({ products: limitedProducts });
    dispatch(setCatalogProducts(limitedProducts));
    dispatch(setCatalogBuilderDirty(true));
    setTemplateApplied({ category: watchedProductSegment, type: "product" });
  }, [watchedProductSegment, mode, form, dispatch, productItemLimit]);

  // Sync contact prefix when Country (address) changes
  useEffect(() => {
    if (!watchedCountry) return;
    const code = getPhoneCodeForCountry(watchedCountry);
    const currentPrefix = form.getFieldValue("phonePrefix");
    if (currentPrefix === code) return;
    const digits = form.getFieldValue("phoneDigits") || "";
    form.setFieldsValue({
      phonePrefix: code,
      phoneDigits: digits,
      phone: userNameDisplayFromPrefix(code, digits),
    });
  }, [watchedCountry, form]);

  useEffect(() => {
    if (contactResendCountdown <= 0) return undefined;
    const timer = setTimeout(() => {
      setContactResendCountdown((countdown) => countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [contactResendCountdown]);

  useEffect(() => {
    setContactOtp("");
    setContactShowOtpInput(false);
    setContactVerificationToken(null);
    setContactNumberVerified(false);
  }, [watchedPhonePrefix, watchedPhoneDigits]);

  useEffect(() => {
    if (!visibleStoreType.includes("P")) {
      form.setFieldsValue({ productSegment: undefined });
    }
    if (!visibleStoreType.includes("S")) {
      form.setFieldsValue({ serviceSegment: undefined });
    }
  }, [visibleStoreType, form]);

  const zipLookupHydratedRef = useRef(false);
  useEffect(() => {
    const zip = String(watchedZip || "").trim();
    if (!/^[0-9]{5,6}$/.test(zip)) {
      setZipLookupLoading(false);
      setZipLookupFailed(false);
      return;
    }

    if (zipLookupHydratedRef.current) {
      zipLookupHydratedRef.current = false;
      return;
    }

    const selectedCountryName = form.getFieldValue("country") || "India";
    const iso2 = countryNameToIsoCode[selectedCountryName];
    if (!iso2) {
      setZipLookupFailed(true);
      return;
    }

    const controller = new AbortController();
    const debounceTimer = setTimeout(() => {
      const lookupZip = async () => {
        setZipLookupLoading(true);
        setZipLookupFailed(false);
        try {
          // India: use india-post pincode API with custom response shape.
          if (selectedCountryName === "India") {
            const response = await fetch(
              `https://api.postalpincode.in/pincode/${zip}`,
              { signal: controller.signal }
            );
            if (!response.ok) {
              throw new Error("India pincode lookup failed");
            }
            const payload = await response.json();
            const firstResult = Array.isArray(payload) ? payload[0] : null;
            const offices = Array.isArray(firstResult?.PostOffice) ? firstResult.PostOffice : [];
            const preferredOffice =
              offices.find((office) => String(office?.DeliveryStatus || "").toLowerCase() === "delivery") ||
              offices[0] ||
              null;

            if (!preferredOffice) {
              throw new Error("No PostOffice found for pincode");
            }

            const nextCity = preferredOffice?.District || preferredOffice?.Name || "";
            const nextState = preferredOffice?.State || "";
            const nextCountry = preferredOffice?.Country || "India";

            form.setFieldsValue({
              city: nextCity || form.getFieldValue("city") || "",
              state: nextState || form.getFieldValue("state") || "",
              country: nextCountry || selectedCountryName,
            });
            setZipLookupFailed(false);
            return;
          }

          // Other countries: fallback to zippopotam.us
          const response = await fetch(
            `https://api.zippopotam.us/${iso2.toLowerCase()}/${zip}`,
            { signal: controller.signal }
          );
          if (!response.ok) {
            throw new Error("Zip lookup failed");
          }
          const payload = await response.json();
          const firstPlace = Array.isArray(payload?.places) ? payload.places[0] : null;
          const nextCity = firstPlace?.["place name"] || "";
          const nextState = firstPlace?.state || firstPlace?.["state abbreviation"] || "";
          const nextCountry = payload?.country || selectedCountryName;

          if (nextCity || nextCountry || nextState) {
            form.setFieldsValue({
              city: nextCity || form.getFieldValue("city") || "",
              state: nextState || form.getFieldValue("state") || "",
              country: nextCountry || selectedCountryName,
            });
            setZipLookupFailed(false);
          }
        } catch (error) {
          if (error?.name !== "AbortError") {
            setZipLookupFailed(true);
          }
        } finally {
          setZipLookupLoading(false);
        }
      };

      lookupZip();
    }, 350);

    return () => {
      clearTimeout(debounceTimer);
      controller.abort();
    };
  }, [watchedZip, watchedCountry, form, countryNameToIsoCode]);

  useEffect(() => {
    const currentGeocode = normalizeGeocode(watchedGeocode);
    if (currentGeocode) return;

    const zip = String(watchedZip || "").trim();
    const country = String(watchedCountry || "").trim();
    if (!/^[0-9]{5,6}$/.test(zip) || !country) return;

    const controller = new AbortController();
    const debounceTimer = setTimeout(async () => {
      try {
        const q = `${country} ${zip}`;
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(q)}`,
          { signal: controller.signal }
        );
        if (res.ok) {
          const data = await res.json();
          const first = Array.isArray(data) ? data[0] : null;
          if (first?.lat && first?.lon) {
            form.setFieldsValue({ geocode: JSON.stringify({ lat: Number(Number(first.lat).toFixed(6)), lon: Number(Number(first.lon).toFixed(6)) }) });
          }
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }, 800);

    return () => { clearTimeout(debounceTimer); controller.abort(); };
  }, [watchedZip, watchedCountry, watchedGeocode, form]);

  const locationPickerAddress = [watchedValues?.address, watchedLocality, watchedCity, watchedState, watchedCountry].filter(Boolean).join(", ");

  useEffect(() => {
    if (mode === "edit") return;
    if (initialValues?.phone) return;
    const parsed = getDefaultPhoneFromDecodedToken(decodedToken);
    if (!parsed) return;
    const existingDigits = form.getFieldValue("phoneDigits");
    if (existingDigits) return;
    form.setFieldsValue({
      phonePrefix: parsed.prefix,
      phoneDigits: parsed.digits,
      phone: userNameDisplayFromPrefix(parsed.prefix, parsed.digits),
    });
  }, [mode, initialValues?.phone, decodedToken, form]);

  useEffect(() => {
    const prefix = watchedPhonePrefix || "+91";
    const digits = watchedPhoneDigits ?? "";
    form.setFieldsValue({
      phone: userNameDisplayFromPrefix(prefix, digits),
    });
  }, [watchedPhonePrefix, watchedPhoneDigits, form]);
  const [activeItemsTab, setActiveItemsTab] = React.useState(
    visibleStoreType.includes("P") ? "products" : "services"
  );
  const itemsTabBothSegmentsResolvedRef = useRef(false);

  const itemCatalogEnabled = visibleStoreType.length > 0;
  const [mainFormTab, setMainFormTab] = React.useState("metadata");
  const [catalogSearchTerm, setCatalogSearchTerm] = React.useState("");

  useEffect(() => {
    if (!itemCatalogEnabled && mainFormTab === "catalog") {
      setMainFormTab("metadata");
    }
  }, [itemCatalogEnabled, mainFormTab]);

  // Get current counts
  const currentProductsCount = catalogProducts.length;
  const currentServicesCount = catalogServices.length;
  const productLimitReached = productItemLimit <= 0 ||
    currentProductsCount >= productItemLimit;
  const serviceLimitReached = serviceItemLimit <= 0 ||
    currentServicesCount >= serviceItemLimit;
  const catalogWithinPlanLimits = React.useCallback(() => {
    if (productItemLimit <= 0 || serviceItemLimit <= 0) {
      message.error(CATALOG_LIMIT_LABELS.loading);
      return false;
    }
    if (currentProductsCount > productItemLimit) {
      message.error(CATALOG_LIMIT_LABELS.productLimit(productItemLimit));
      return false;
    }
    if (currentServicesCount > serviceItemLimit) {
      message.error(CATALOG_LIMIT_LABELS.serviceLimit(serviceItemLimit));
      return false;
    }
    return true;
  }, [
    currentProductsCount,
    currentServicesCount,
    message,
    productItemLimit,
    serviceItemLimit,
  ]);

  // Auto-clear product/service arrays when switching store types
  useEffect(() => {
    if (!visibleStoreType.includes("P") && catalogProducts.length > 0) {
      dispatch(setCatalogProducts([]));
      dispatch(setCatalogBuilderDirty(true));
      form.setFieldsValue({ products: [] });
    }
    if (!visibleStoreType.includes("S") && catalogServices.length > 0) {
      dispatch(setCatalogServices([]));
      dispatch(setCatalogBuilderDirty(true));
      form.setFieldsValue({ services: [] });
    }
  }, [visibleStoreType, catalogProducts.length, catalogServices.length, dispatch, form]);

  // Load Initial Values (Edit Mode)
  useEffect(() => {
    if (expandSettings) {
      setActiveCollapseKey(["settings"]); // expand settings, collapse details
    }
  }, [expandSettings]);

  useEffect(() => {
    const allowedTabs = new Set();
    if (visibleStoreType.includes("P")) allowedTabs.add("products");
    if (visibleStoreType.includes("S")) allowedTabs.add("services");

    if (allowedTabs.size === 0) return;

    if (
      !itemsTabBothSegmentsResolvedRef.current &&
      allowedTabs.has("products") &&
      allowedTabs.has("services")
    ) {
      itemsTabBothSegmentsResolvedRef.current = true;
      setActiveItemsTab("products");
      return;
    }

    if (allowedTabs.has(activeItemsTab)) return;

    if (allowedTabs.has("products")) {
      setActiveItemsTab("products");
      return;
    }
    if (allowedTabs.has("services")) {
      setActiveItemsTab("services");
    }
  }, [activeItemsTab, visibleStoreType]);

  const resolveErrorSection = (namePath = []) => {
    const [root] = namePath;
    if (
      [
        "name",
        "storeType",
        "address",
        "city",
        "state",
        "country",
        "zip",
        "phone",
        "phonePrefix",
        "phoneDigits",
        "productSegment",
        "serviceSegment",
      ].includes(root)
    ) {
      return "details";
    }
    if (
      [
        "bizPrivate",
        "bizPasskey",
        "confirmPasskey",
        "bizOpenStatus",
        "bizLive",
        "flashEnabled",
        "flashMessage",
        "specialDiscount",
      ].includes(root)
    ) {
      return "settings";
    }
    if (["products"].includes(root)) {
      return "products";
    }
    if (["services"].includes(root)) {
      return "services";
    }
    return null;
  };

  const handleFinishFailed = ({ errorFields }) => {
    if (!errorFields?.length) return;
    const firstError = errorFields[0];
    const errorMsg = firstError.errors?.[0] || `Validation failed: ${firstError.name?.join(".")}`;
    message.warning(errorMsg);
    const targetSection = resolveErrorSection(firstError.name);

    if (
      (firstError.name?.[0] === "bizPasskey" ||
        firstError.name?.[0] === "confirmPasskey") &&
      form.getFieldValue("bizPrivate")
    ) {
      scrollToTop(); setStep(2);
      setPasskeyPromptValue("");
      setPasskeyPromptOpen(true);
      return;
    }

    if (targetSection === "details") {
      scrollToTop(); setStep(1);
    } else if (targetSection === "settings") {
      scrollToTop(); setStep(2);
    } else if (targetSection === "products" || targetSection === "services") {
      scrollToTop(); setStep(3);
      setActiveItemsTab(targetSection);
    }

    setTimeout(() => {
      form.scrollToField(firstError.name, {
        behavior: "smooth",
        block: "center",
      });
    }, 50);
  };

  const handleContactVerify = async (action = "") => {
    const prefix = form.getFieldValue("phonePrefix");
    const mobileVal = form.getFieldValue("phoneDigits");

    if (action === "Edit") {
      setContactEditMode(true);
      if (contactNumberVerified) {
        setContactNumberVerified(false);
        setContactShowOtpInput(false);
        setContactVerificationToken(null);
        setContactOtp("");
      }
      return;
    }

    if (contactNumberVerified && action !== "Resend") {
      setContactNumberVerified(false);
      setContactShowOtpInput(false);
      setContactVerificationToken(null);
      setContactOtp("");
      return;
    }
    if (action === "Resend") {
      setContactOtp("");
    }

    if (!isMobileLengthValid(mobileVal)) return;

    const isResend = action === "Resend";
    if (isResend) setContactResendLoading(true);
    else setContactVerifyLoading(true);
    try {
      const response = await axios.post(
        userRegistration,
        Encrypt({
          action: "verifyMobile",
          mobile: mobileForApi(prefix, mobileVal),
        }),
        { headers: { "Content-Type": "application/json" } }
      );
      const verifyToken = response.data?.token;
      if (verifyToken) {
        setContactShowOtpInput(true);
        setContactVerificationToken(verifyToken);
        setContactNumberVerified(true);
        setContactResendCountdown(46);
        setContactEditMode(false);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "Unexpected error occurred. Please try again later.";
      form.setFields([{ name: "phoneDigits", errors: [errorMessage] }]);
    } finally {
      if (isResend) setContactResendLoading(false);
      else setContactVerifyLoading(false);
    }
  };

  useEffect(() => {
    if (!initialValues) {
      dispatch(clearBizEditBaseline());
      return;
    }

    setDeletedItemIds([]);
    const hydrateItemRow = (row) => {
      const p = row || {};
      return {
        [HUB_ITEM.ItemId]: p[HUB_ITEM.ItemId],
        [HUB_ITEM.ItemName]: p[HUB_ITEM.ItemName],
        [HUB_ITEM.Description]: p[HUB_ITEM.Description],
        [HUB_ITEM.BasePrice]: p[HUB_ITEM.BasePrice],
        [HUB_ITEM.Variants]: Array.isArray(p[HUB_ITEM.Variants])
          ? p[HUB_ITEM.Variants]
          : [],
        [HUB_ITEM.Customisations]: Array.isArray(p[HUB_ITEM.Customisations])
          ? p[HUB_ITEM.Customisations]
          : [],
        [HUB_ITEM.Discount]: p[HUB_ITEM.Discount],
        [HUB_ITEM.VideoUrls]: p[HUB_ITEM.VideoUrls] ?? [],
        [HUB_ITEM.ItemImages]: Array.isArray(p[HUB_ITEM.ItemImages])
          ? p[HUB_ITEM.ItemImages]
          : [],
        [HUB_ITEM.RequiresPrebooking]: p[HUB_ITEM.RequiresPrebooking] === true,
        [HUB_ITEM.ItemAvailable]: p[HUB_ITEM.ItemAvailable] !== false,
        extraFields: p.extraFields,
        fileRequired: p.fileRequired,
        filePlaceholder: p.filePlaceholder,
        isPrivate: p.isPrivate,
        _pendingImages: p._pendingImages,
      };
    };

    const updatedProducts =
      initialValues.products?.map((product) => hydrateItemRow(product)) || [];

    const updatedServices =
      initialValues.services?.map((service) => hydrateItemRow(service)) || [];

    const { BizSettings, ...restInitialValues } = initialValues;
    const settingsFromBackend = BizSettings;
    const inMemoryPasskey =
      typeof initialValues.bizPasskey === "string"
        ? initialValues.bizPasskey.trim()
        : "";
    const initialBizPrivate = settingsFromBackend?.bizPrivate ?? initialValues.bizPrivate ?? false;

    // Reset to Public when the active plan does not include private stores.
    const finalBizPrivate =
      privateStoreAllowed === false && initialBizPrivate ? false : initialBizPrivate;

    const settingsValues = settingsFromBackend || {
      bizPrivate: finalBizPrivate,
      bizPasskey: inMemoryPasskey || initialValues.bizPasskey, // In-memory from Stores unlock, not persisted in DB
      bizOpenStatus: initialValues.bizOpenStatus,
      bizLive:
        mode === "edit"
          ? initialValues.bizLive === true
          : initialValues.bizLive === true,
      flashEnabled: initialValues.flashEnabled,
      flashMessage: initialValues.flashMessage,
      specialDiscount: initialValues.specialDiscount,
      deliveryOptions: initialValues.deliveryOptions || ["PICKUP"],
      paymentMethods: normalizePaymentMethods(initialValues.paymentMethods, { kycVerified }),
      serviceCoverageScope: initialValues.serviceCoverageScope || "ANYWHERE",
      serviceCoverageAreas: Array.isArray(initialValues.serviceCoverageAreas)
        ? initialValues.serviceCoverageAreas : [],
      prebookingGroups: initialValues.prebookingGroups || "[]",
    };

    if (settingsFromBackend && settingsValues.bizLive === undefined) {
      settingsValues.bizLive = settingsFromBackend.bizLive === true;
    }

    if (!settingsValues.serviceCoverageScope) {
      settingsValues.serviceCoverageScope = "ANYWHERE";
    }

    settingsValues.paymentMethods = normalizePaymentMethods(settingsValues.paymentMethods, { kycVerified });
    settingsValues.socialFootprints = {
      youtube: "",
      instagram: "",
      facebook: "",
      linkedin: "",
      x: "",
      ...normalizeSocialFootprints(settingsValues.socialFootprints),
    };

    if (inMemoryPasskey) {
      settingsValues.bizPasskey = inMemoryPasskey;
      settingsValues.confirmPasskey = inMemoryPasskey;
    }

    // Override bizPrivate if it was reset
    if (settingsFromBackend) {
      settingsValues.bizPrivate = finalBizPrivate;
    }

    if (mode === "edit") {
      const mergedForMeta = {
        ...restInitialValues,
        productSegment: restInitialValues.ProductSegment ?? restInitialValues.productSegment ?? "",
        serviceSegment: restInitialValues.ServiceSegment ?? restInitialValues.serviceSegment ?? "",
        ...settingsValues,
      };
      dispatch(
        setBizEditBaseline({
          metadata: buildMetadataSnapshot(mergedForMeta, settingsValues),
          productsKey: sortedItemsComparableKey(updatedProducts),
          servicesKey: sortedItemsComparableKey(updatedServices),
          itemsGalleryLinkageKey: itemsGalleryLinkageComparableKey(
            updatedProducts,
            updatedServices
          ),
          baselineProducts: updatedProducts,
          baselineServices: updatedServices,
        })
      );
    } else {
      dispatch(clearBizEditBaseline());
    }

    const parsedPhoneFromStore =
      restInitialValues.phone != null && String(restInitialValues.phone).trim() !== ""
        ? parseApiMobileStringToPrefixAndDigits(String(restInitialValues.phone))
        : null;
    const contactFieldDefaults = parsedPhoneFromStore
      ? {
          phonePrefix: parsedPhoneFromStore.prefix,
          phoneDigits: parsedPhoneFromStore.digits,
          phone: userNameDisplayFromPrefix(
            parsedPhoneFromStore.prefix,
            parsedPhoneFromStore.digits
          ),
        }
      : {};

    form.setFieldsValue({
      ...restInitialValues,
      productSegment: restInitialValues.ProductSegment ?? restInitialValues.productSegment ?? "",
      serviceSegment: restInitialValues.ServiceSegment ?? restInitialValues.serviceSegment ?? "",
      mapUrl: initialValues.mapUrl || "",
      geocode: geocodeFormValue(initialValues.geocode),
      ...settingsValues,
      ...contactFieldDefaults,
      products: updatedProducts,
      services: updatedServices,
      bizImageKey: initialValues.bizImageKey ?? "",
    });
    dispatch(setCatalogProducts(updatedProducts));
    dispatch(setCatalogServices(updatedServices));
    zipLookupHydratedRef.current = true;

    // Init prebooking groups from form values
    const raw = form.getFieldValue("prebookingGroups");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          setPrebookingGroups(parsed.map(g => ({
            key: Date.now() + Math.random(),
            datePickerValue: g.dates ? g.dates.map(d => dayjs(d)) : [],
            slots: g.slots || [],
            duration: g.duration || 60,
            breakDuration: g.breakDuration || 0,
            maxOrdersPerSlot: g.maxOrdersPerSlot ?? null
          })));
        } else {
          setPrebookingGroups([]);
        }
      } catch(e) {
        setPrebookingGroups([]);
      }
    }
  }, [initialValues, form, privateStoreAllowed, kycVerified, mode, dispatch]);

  const handleItemRemove = (itemId, itemType) => {
    if (!itemId || !itemType) return;
    setDeletedItemIds((prev) =>
      prev.some(
        (e) => e[HUB_ITEM.ItemId] === itemId && e[HUB_ITEM.ItemType] === itemType
      )
        ? prev
        : [...prev, { [HUB_ITEM.ItemId]: itemId, [HUB_ITEM.ItemType]: itemType }]
    );
  };

  // Prevent Private when the catalog module is not enabled.
  useEffect(() => {
    if (privateStoreAllowed !== false) return;
    const currentBizPrivate = form.getFieldValue("bizPrivate");
    if (currentBizPrivate === true) {
      form.setFieldsValue({ bizPrivate: false });
    }
  }, [privateStoreAllowed, form]);

  // Submit Handler
  const onFinish = async (values) => {
    if (!catalogWithinPlanLimits()) {
      setStep(3);
      return;
    }
    if (values.bizLive === true && !hasCatalogItems()) {
      message.error("Add at least one product or service to the catalogue before marking the store Live.");
      setStep(3);
      return;
    }
    setLoading(true);

    try {
      const storeType = Array.isArray(values.storeType)
        ? values.storeType.sort().join(",")
        : values.storeType;

      // Extract Store Settings into a nested object
      const {
        bizPrivate,
        bizPasskey,
        confirmPasskey,
        bizOpenStatus,
        bizLive,
        flashEnabled,
        flashMessage,
        specialDiscount,
        businessDocs,
        deliveryOptions,
        paymentMethods,
        serviceCoverageScope,
        serviceCoverageAreas,
        prebookingGroups,
        socialFootprints,
        phonePrefix,
        phoneDigits,
        ...restWithSyncedPhone
      } = values;
      const { phone: ignoredSyncedPhone, ...restValues } = restWithSyncedPhone;
      void ignoredSyncedPhone;

      const phoneDisplay = userNameDisplayFromPrefix(phonePrefix, phoneDigits);
      const storeTypeValues = Array.isArray(values.storeType)
        ? values.storeType
        : values.storeType
          ? String(values.storeType).split(",").map((s) => s.trim()).filter(Boolean)
          : [];
      const valuesForTarget = {
        ...values,
        phone: phoneDisplay,
        products: catalogProductsRef.current,
        services: catalogServicesRef.current,
      };
      const effectiveMode = createdBizId ? "edit" : mode;

      const BizSettings = {
        bizPrivate,
        bizOpenStatus,
        bizLive: bizLive === true,
        flashEnabled,
        flashMessage,
        specialDiscount,
        businessDocs: (businessDocs || []).slice(0, BUSINESS_DOCS_CONFIG.MAX_DOCS_PER_BIZ),
        deliveryOptions: deliveryOptions?.length ? deliveryOptions : ["PICKUP"],
        paymentMethods: normalizePaymentMethods(paymentMethods, { kycVerified }),
        serviceCoverageScope: serviceCoverageScope || "ANYWHERE",
        serviceCoverageAreas: Array.isArray(serviceCoverageAreas) ? serviceCoverageAreas : [],
        prebookingGroups: prebookingGroups || "[]",
        socialFootprints: normalizeSocialFootprints(socialFootprints),
      };

      const updateTarget = computeUpdateTarget({
        mode,
        values: valuesForTarget,
        BizSettings,
        deletedItemIds: mergeImplicitDeletionsFromStoreTypeChange({ mode, storeTypeValues, deletedItemIds, baseline }),
        baseline,
        catalogDirty: catalogBuilderDirty,
      });
      if (updateTarget === "NONE") {
        form.resetFields();
        navigate(-1);
        setLoading(false);
        return;
      }

      const normalizedPasskey =
        typeof bizPasskey === "string" ? bizPasskey.trim() : "";

      if (bizPrivate && privateStoreAllowed !== true) {
        message.error(
          CATALOG_LIMIT_LABELS.privateStoreError
        );
        setLoading(false);
        return;
      }

      if (bizPrivate && !normalizedPasskey && !(mode === "edit" && privateEditUnlocked)) {
        setPasskeyPromptValue("");
        setPasskeyPromptOpen(true);
        setLoading(false);
        return;
      }

      if (normalizedPasskey) {
        BizSettings.bizPasskey = normalizedPasskey;
      } else if (bizPrivate) {
        const existingHash = getBizPasskeyHashFromSettings(initialValues?.BizSettings);
        if (existingHash) {
          BizSettings.BizPasskeyHash = existingHash;
        }
      }

      const currentContactApi = mobileForApi(phonePrefix, phoneDigits);
      const contactNeedsWhatsappVerify =
        isMobileLengthValid(phoneDigits) &&
        (contactNumberBaselineApi === null ||
          currentContactApi !== contactNumberBaselineApi);

      if (contactNeedsWhatsappVerify) {
        if (!contactVerificationToken || String(contactOtp).trim().length < 6) {
          message.warning(
            "Verify your contact number with the WhatsApp code before saving."
          );
          setLoading(false);
          return;
        }
      }

      const mergedDeletedItemIds = mergeImplicitDeletionsFromStoreTypeChange({
        mode,
        storeTypeValues,
        deletedItemIds,
        baseline,
      });

      const toNum = (v) => {
        if (v === undefined || v === null || v === "") return undefined;
        const n = Number(v);
        return Number.isFinite(n) ? n : undefined;
      };

      const transformItems = (items, itemType) =>
        items?.map((item) => {
          const {
            bizId,
            _pendingImages,
            extraFields,
            fileRequired,
            filePlaceholder,
            ...raw
          } = item || {};

          const id = raw[HUB_ITEM.ItemId] ?? GenerateShortID(6);

          const imgSrc = raw[HUB_ITEM.ItemImages];
          const imagePayload = Array.isArray(imgSrc)
            ? imgSrc
                .map((img) => {
                  if (typeof img === "string") {
                    const rawKey = img.startsWith(CDN_IMAGE_BASE)
                      ? img.slice(CDN_IMAGE_BASE.length).replace(/^\//, "")
                      : img;
                    const key = basenameFromStorageKey(rawKey) || rawKey;
                    return key ? { imageKey: key } : null;
                  }
                  return img?.imageKey
                    ? {
                        imageKey: basenameFromStorageKey(img.imageKey) || img.imageKey,
                        ...(img?.originalName && { originalName: img.originalName }),
                      }
                    : null;
                })
                .filter(Boolean)
            : [];

          const resolvedBase =
            toNum(raw[HUB_ITEM.BasePrice]) ??
            toNum(raw.basePrice) ??
            toNum(raw.cost);

          const variantList = raw[HUB_ITEM.Variants] ?? raw.variants ?? [];
          const customisationList =
            raw[HUB_ITEM.Customisations] ?? raw.customisations ?? [];

          const discRaw = raw[HUB_ITEM.Discount] ?? raw.discount;

          const baseItem = {
            [HUB_ITEM.ItemId]: id,
            [HUB_ITEM.ItemType]: itemType,
            [HUB_ITEM.ItemName]: raw[HUB_ITEM.ItemName] ?? raw.name,
            [HUB_ITEM.Description]: raw[HUB_ITEM.Description] ?? raw.description,
            [HUB_ITEM.BasePrice]: resolvedBase,
            [HUB_ITEM.Variants]: Array.isArray(variantList) ? variantList : [],
            [HUB_ITEM.Customisations]: Array.isArray(customisationList)
              ? customisationList
              : [],
            [HUB_ITEM.Discount]:
              discRaw !== undefined && discRaw !== null && discRaw !== ""
                ? Number(discRaw)
                : undefined,
            [HUB_ITEM.RequiresPrebooking]: raw[HUB_ITEM.RequiresPrebooking] === true,
            [HUB_ITEM.ItemAvailable]: raw[HUB_ITEM.ItemAvailable] !== false,
            ...(imagePayload.length > 0
              ? { [HUB_ITEM.ItemImages]: imagePayload }
              : {}),
            ...(extraFields &&
            typeof extraFields === "object" &&
            Object.keys(extraFields).length > 0
              ? { extraFields }
              : {}),
            ...(fileRequired !== undefined ? { fileRequired } : {}),
            ...(filePlaceholder !== undefined ? { filePlaceholder } : {}),
          };

          return baseItem;
        });

      const hubId = selectedHubProfile?.PK;
      const domain = selectedHubProfile?.Domain ?? "";

      let bizId;
      if (initialValues?.bizId && mode === 'edit') {
        bizId = initialValues.bizId;
      } else {
        const baseName = String(values.name ?? "")
          .replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase();
        let counter = 1;
        let tempBizId = baseName;
        const rawExisting =
          location?.state?.existingBizIds ?? existingBizIds ?? allStores;
        const takenBizIds = Array.isArray(rawExisting) ? rawExisting : [];

        while (takenBizIds.includes(tempBizId)) {
          tempBizId = `${baseName}-${counter}`;
          counter++;
        
          // Safety check to skip infinite loop
          if (counter > 200) {
            tempBizId = `${baseName}-${Date.now()}`;
            break;
          }
        }
        bizId = tempBizId
      }

      const geocodePayload = normalizeGeocode(values.geocode);

      const shop = {
        bizId: createdBizId || bizId,
        domain,
        ...restValues,
        locality: values.locality || "",
        state: values.state || "",
        phone: phoneDisplay,
        storeType,
        products: transformItems(catalogProductsRef.current, "PRODUCT") || [],
        services: transformItems(catalogServicesRef.current, "SERVICE") || [],
        mapUrl: values.mapUrl || null,
        geocode: geocodePayload,
        IsPrivate: !!bizPrivate,
        BizSettings,
        updateTarget,
        ...(values.bizImageKey ? { bizImageKey: values.bizImageKey } : {}),
        ...(effectiveMode === "edit"
          ? {
              version: bizVersionRef.current || 1,
              deletedItemIds: mergedDeletedItemIds,
              ...(createdHubId ? { hubId: createdHubId } : {}),
            }
          : {}),
        ...(hubId ? { hubId } : {}),
      };

      const saveRes = await handleAddOrEditShop({ shop, mode: effectiveMode, token });
      if (saveRes != null) {
        const newVer =
          saveRes?.data?.Items?.[0]?.version ??
          saveRes?.data?.version ??
          saveRes?.data?.Attributes?.version ??
          saveRes?.version;
        if (newVer) bizVersionRef.current = newVer;
        else if (effectiveMode === "edit") bizVersionRef.current = (bizVersionRef.current || 1) + 1;
        else bizVersionRef.current = 1;
      }

      if (saveRes != null && hubId && bizId && token) {
        try {
          const prefix = mediaPath({ entity: "item", fileType: "media", hubId, bizId });
          if (prefix) invalidateManifestFetchCacheForPath(prefix);
        } catch (mErr) { console.warn("Manifest cache invalidate", mErr); }

        if (mode === "edit") {
          const nextItemsGalleryLinkageKey = itemsGalleryLinkageComparableKey(
            shop.products,
            shop.services
          );
          const baselineItemsGalleryLinkageKey = baseline?.itemsGalleryLinkageKey;
          const bothItemsGalleryLinkageEmpty =
            nextItemsGalleryLinkageKey === "" &&
            (baselineItemsGalleryLinkageKey === "" ||
              baselineItemsGalleryLinkageKey === undefined ||
              baselineItemsGalleryLinkageKey === null);
          const itemsGalleryLinkageUnchanged =
            baselineItemsGalleryLinkageKey !== undefined &&
            baselineItemsGalleryLinkageKey !== null &&
            nextItemsGalleryLinkageKey === baselineItemsGalleryLinkageKey;
          const shouldSyncItemsGalleryManifest =
            !bothItemsGalleryLinkageEmpty && !itemsGalleryLinkageUnchanged;

          if (shouldSyncItemsGalleryManifest) {
            try {
              await syncItemsGalleryLinkedItemIds({
                token,
                hubId,
                bizId,
                products: shop.products,
                services: shop.services,
                bizPrivate: !!bizPrivate,
                manifestCrypto: null,
              });
            } catch (syncError) {
              console.warn("syncItemsGalleryLinkedItemIds", syncError);
            }
          }
        }
      }
      form.resetFields();
      if (mode === "create" && location.state?.guidedJourneyStarted) {
        navigate("/hub", {
          replace: true,
          state: {
            guidedJourneyResult: {
              firstBusiness: location.state?.guidedFirstBusiness === true,
              isDraft: BizSettings.bizLive !== true,
              bizId: shop.bizId,
              hubId,
            },
          },
        });
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error("Error saving biz:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUnlockConfirm = () => {
    const trimmed = editUnlockPasskeyInput.trim();
    if (!trimmed) {
      message.warning("Please enter passkey.");
      return;
    }
    const storedHash = getBizPasskeyHashFromSettings(initialValues?.BizSettings);
    if (!storedHash || !compareStorePasskey(trimmed, storedHash)) {
      message.error("Invalid passkey.");
      return;
    }
    setPrivateEditUnlocked(true);
    setEditUnlockModalOpen(false);
    setEditUnlockPasskeyInput("");
    message.success("You can edit this private Biz.");
  };

  const handlePasskeyPromptConfirm = () => {
    if (!passkeyPromptValue) {
      message.warning("Please enter passkey.");
      return;
    }
    const normalizedPasskey = passkeyPromptValue.trim();
    if (!normalizedPasskey) {
      message.warning("Please enter passkey.");
      return;
    }
    form.setFieldsValue({
      bizPasskey: normalizedPasskey,
      confirmPasskey: normalizedPasskey,
    });
    setPasskeyPromptOpen(false);
    form.submit();
  };

  const handleGoForward = async () => {
    scrollToTop();
    if (step < TOT_STEPS) {
      if (step === 1) {
        const fieldsToValidate = ["name", "storeType", "address", "locality", "city", "state", "country", "zip", "phoneDigits"];
        const currentType = visibleStoreType;
        if (currentType.length === 0 || currentType.includes("P")) fieldsToValidate.push("productSegment");
        if (currentType.length === 0 || currentType.includes("S")) fieldsToValidate.push("serviceSegment");
        form.validateFields(fieldsToValidate)
          .then(() => {
            const phonePrefix = form.getFieldValue("phonePrefix") || "+91";
            const phoneDigits = form.getFieldValue("phoneDigits") || "";
            const currentContactApi = mobileForApi(phonePrefix, phoneDigits);
            const contactNeedsWhatsappVerify =
              isMobileLengthValid(phoneDigits) &&
              (contactNumberBaselineApi === null ||
                currentContactApi !== contactNumberBaselineApi);

            if (
              contactNeedsWhatsappVerify &&
              (!contactVerificationToken || String(contactOtp).trim().length < 6)
            ) {
              const verifyMessage =
                "Contact number changed. Verify with WhatsApp code before continuing.";
              message.error(verifyMessage);
              form.setFields([{ name: "phoneDigits", errors: [verifyMessage] }]);
              return;
            }

            setStep(2);
          })
          .catch(() => {});
      } else if (step === 2) {
        setStep2Loading(true);
        try {
          await form.validateFields();
          const values = form.getFieldsValue(true);
          const phonePrefix = values.phonePrefix || "+91";
          const phoneDigits = values.phoneDigits || "";
          const phoneDisplay = userNameDisplayFromPrefix(phonePrefix, phoneDigits);
          const storeType = Array.isArray(values.storeType) ? values.storeType.sort().join(",") : values.storeType;
          const { bizPrivate, bizPasskey, bizOpenStatus, bizLive, flashEnabled, flashMessage, specialDiscount, businessDocs, deliveryOptions, paymentMethods, socialFootprints } = values;
          const { serviceCoverageScope, serviceCoverageAreas, prebookingGroups } = values;
          const canRemainLive = hasCatalogItems();
          if (bizLive === true && !canRemainLive) {
            form.setFieldsValue({ bizLive: false });
            message.warning("The store was kept as Draft because the catalogue has no products or services.");
          }
          const BizSettings = {
            bizPrivate: !!bizPrivate,
            bizOpenStatus: bizOpenStatus ?? true,
            bizLive: bizLive === true && canRemainLive,
            flashEnabled: !!flashEnabled,
            flashMessage: flashMessage || "",
            specialDiscount,
            businessDocs: (businessDocs || []).slice(0, BUSINESS_DOCS_CONFIG.MAX_DOCS_PER_BIZ),
            deliveryOptions: deliveryOptions?.length ? deliveryOptions : ["PICKUP"],
            paymentMethods: normalizePaymentMethods(paymentMethods, { kycVerified }),
            serviceCoverageScope: serviceCoverageScope || "ANYWHERE",
            serviceCoverageAreas: Array.isArray(serviceCoverageAreas) ? serviceCoverageAreas : [],
            prebookingGroups: prebookingGroups || "[]",
            socialFootprints: normalizeSocialFootprints(socialFootprints),
          };
          const normalizedStep2Passkey =
            typeof bizPasskey === "string" ? bizPasskey.trim() : "";
          if (normalizedStep2Passkey) {
            BizSettings.bizPasskey = normalizedStep2Passkey;
          } else if (bizPrivate) {
            const existingHash = getBizPasskeyHashFromSettings(initialValues?.BizSettings);
            if (existingHash) {
              BizSettings.BizPasskeyHash = existingHash;
            } else {
              message.error("Passkey is required for a private store.");
              setStep2Loading(false);
              return;
            }
          }
          const hubId = selectedHubProfile?.PK;
          const domain = selectedHubProfile?.Domain ?? "";

          const currentSnapshot = { name: values.name, storeType: Array.isArray(values.storeType) ? [...values.storeType].sort().join(",") : values.storeType, address: values.address, locality: values.locality || "", city: values.city, state: values.state || "", country: values.country, zip: values.zip, mapUrl: values.mapUrl || "", geocode: values.geocode || "", phone: phoneDisplay, productSegment: values.productSegment || "", serviceSegment: values.serviceSegment || "", bizPrivate: !!bizPrivate, bizImageKey: values.bizImageKey || "", BizSettings };

          const existingBizId = createdBizId || initialValues?.bizId;
          let bizId, effectiveMode;
          if (existingBizId) {
            bizId = existingBizId;
            effectiveMode = "edit";
            if (step2SnapshotRef.current && JSON.stringify(currentSnapshot) === JSON.stringify(step2SnapshotRef.current)) {
              setStep2Loading(false); setStep(3); return;
            }
          } else {
            const baseName = String(values.name ?? "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            const rawExisting = location?.state?.existingBizIds ?? existingBizIds ?? allStores;
            const takenBizIds = Array.isArray(rawExisting) ? rawExisting : [];
            bizId = baseName; let counter = 1;
            while (takenBizIds.includes(bizId)) { bizId = `${baseName}-${counter}`; counter++; if (counter > 200) { bizId = `${baseName}-${Date.now()}`; break; } }
            effectiveMode = "create";
          }

          const geocodePayload = normalizeGeocode(values.geocode);

          const shop = { bizId, domain, name: values.name, storeType, address: values.address, locality: values.locality || "", city: values.city, state: values.state || "", country: values.country, zip: values.zip, phone: phoneDisplay, productSegment: values.productSegment || "", serviceSegment: values.serviceSegment || "", mapUrl: values.mapUrl || null, geocode: geocodePayload, products: [], services: [], activeFlag: "active", IsPrivate: !!bizPrivate, BizSettings, extras: buildTypesenseExtras(values), ...(hubId ? { hubId } : {}), ...(values.bizImageKey ? { bizImageKey: values.bizImageKey } : {}) };
          if (effectiveMode !== "edit") { shop.version = 1; } else { shop.version = bizVersionRef.current || 1; }
          const saveRes = await handleAddOrEditShop({ shop, mode: effectiveMode, token });
          if (saveRes) {
            step2SnapshotRef.current = currentSnapshot;
            const newVer = saveRes?.data?.Items?.[0]?.version ?? saveRes?.data?.version ?? saveRes?.data?.Attributes?.version ?? saveRes?.version;
            if (newVer) bizVersionRef.current = newVer;
            else if (effectiveMode === "edit") bizVersionRef.current = (bizVersionRef.current || 1) + 1;
            else bizVersionRef.current = 1;
            setCreatedBizId(bizId);
            setCreatedHubId(hubId);
            setStep(3);
            const mergedMeta = {
              ...values,
              phone: phoneDisplay,
              productSegment: values.productSegment || "",
              serviceSegment: values.serviceSegment || "",
            };
            const blProducts = catalogProductsRef.current || [];
            const blServices = catalogServicesRef.current || [];
            dispatch(setBizEditBaseline({
              metadata: buildMetadataSnapshot(mergedMeta, BizSettings),
              productsKey: sortedItemsComparableKey(blProducts),
              servicesKey: sortedItemsComparableKey(blServices),
              itemsGalleryLinkageKey: itemsGalleryLinkageComparableKey(blProducts, blServices),
              baselineProducts: blProducts,
              baselineServices: blServices,
            }));
          }
        } catch (err) { console.error("Step 2 failed", err); message.error("Failed to save. Check all fields."); }
        finally { setStep2Loading(false); }
      }
    }
  };

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (formDirtyRef.current || catalogBuilderDirty || catalogHasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [catalogBuilderDirty, catalogHasUnsavedChanges]);

  const confirmLeave = React.useCallback((navAction) => {
    if (formDirtyRef.current || catalogBuilderDirty || catalogHasUnsavedChanges) {
      pendingNavActionRef.current = navAction;
      setNavigationConfirmOpen(true);
    } else {
      navAction();
    }
  }, [catalogBuilderDirty, catalogHasUnsavedChanges]);

  const handleNavigateAway = React.useCallback(() => {
    if (mode === "create" && location.state?.guidedJourneyStarted) {
      navigate("/hub", {
        replace: true,
        state: { guidedJourneyResult: { cancelled: true } },
      });
      return;
    }
    navigate(-1);
  }, [location.state, mode, navigate]);

  const handleCancelClick = React.useCallback(() => {
    confirmLeave(handleNavigateAway);
  }, [confirmLeave, handleNavigateAway]);

  const handleGoBack = React.useCallback(() => {
    if (step > 1) {
      const navAction = () => { scrollToTop(); setStep(step - 1); };
      if (step === 3) {
        confirmLeave(navAction);
      } else {
        navAction();
      }
    }
  }, [step, confirmLeave]);

  const handleFinishSetupClick = async () => {
    if (builderOpen && productBuilderRef.current?.saveItem) {
      const saved = await productBuilderRef.current.saveItem();
      if (!saved) return;
    } else {
      const draft = productBuilderRef.current?.getItemData();
      if (draft && builderOpen) handleCatalogDraftChange(draft);
    }
    const currentProducts = catalogProductsRef.current;
    const currentServices = catalogServicesRef.current;
    form.setFieldsValue({
      products: currentProducts,
      services: currentServices,
    });
    if (catalogBuilderDirty || catalogHasUnsavedChanges) {
      dispatch(setCatalogProducts(currentProducts));
      dispatch(setCatalogServices(currentServices));
    }
    form.submit();
  };

  const handleStep3SaveClick = async () => {
    if (builderOpen && productBuilderRef.current?.saveItem) {
      const saved = await productBuilderRef.current.saveItem();
      if (!saved) return;
      return;
    }
    await handleCatalogSave();
  };

  const renderProgressBar = () => {
    const stepNames = [BIZ.STEP1_NAME, BIZ.STEP2_NAME, BIZ.STEP3_NAME];
    const stepDescs = [BIZ.STEP1_DESC, BIZ.STEP2_DESC, null];
    return (
      <div className={styles.stepProgressBar}>
        {stepNames.map((name, idx) => {
          const num = idx + 1;
          const isActive = num <= step;
          const isCurrent = num === step;
          return (
            <React.Fragment key={num}>
              <div className={styles.stepProgressItem}>
                <div className={`${styles.stepProgressCircle} ${isActive ? styles.stepProgressCircleActive : styles.stepProgressCircleInactive}`}>
                  {num}
                </div>
                <div className={`${styles.stepProgressName} ${isCurrent ? styles.stepProgressNameActive : ""}`}>
                  {name}
                </div>
                {isCurrent && stepDescs[idx] && (
                  <div className={styles.stepProgressDesc}>{stepDescs[idx]}</div>
                )}
              </div>
              {num < stepNames.length && <div className={styles.stepProgressConnector} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderStep1 = () => (
    <>
      <div className={styles.pageHeader}>{BIZ.PAGE_TITLE}</div>
      {renderProgressBar()}

      {/* \u2500\u2500 Card 1: Business Details \u2500\u2500 */}
      <div className={styles.settingsGroup} style={{ marginBottom: 10 }}>
        <div className={styles.settingsGroupHeader}>
          <div className={styles.settingsGroupIconBox}>{"\ud83c\udfe2"}</div>
          <div className={styles.settingsGroupInfo}>
            <div className={styles.settingsGroupTitle2}>{BIZ.DETAILS_CARD_TITLE}</div>
            <div className={styles.settingsGroupSubtitle}>{BIZ.DETAILS_CARD_SUBTITLE}</div>
          </div>
        </div>

        {/* Row 1: Business Name + Business Type */}
        <div className={styles.step1FieldRow}>
          {/* Business Name */}
          <div className={styles.step1FieldCol}>
            <div className={styles.fieldBlockLabel}>
              <span className={styles.requiredAsterisk}>* </span>{BIZ.NAME_LABEL}
            </div>
            <div className={styles.settingSubLabel} style={{ marginBottom: 6 }}>{BIZ.NAME_SUBLABEL}</div>
            <Form.Item name="name" rules={[{ required: true, message: BIZ.NAME_REQUIRED }]} style={{ marginBottom: 0 }}>
              <Input placeholder={BIZ.NAME_PLACEHOLDER} />
            </Form.Item>
          </div>

          {/* Business Type */}
          <div className={styles.step1FieldCol}>
            <div className={styles.fieldBlockLabel}>
              <span className={styles.requiredAsterisk}>* </span>{BIZ.TYPE_LABEL}
            </div>
            <div className={styles.settingSubLabel} style={{ marginBottom: 6 }}>{BIZ.TYPE_SUBLABEL}</div>
            <div className={styles.optionGroup}>
              {[["P", "\ud83d\uded2", BIZ.TYPE_PRODUCT], ["S", "\ud83d\udece", BIZ.TYPE_SERVICE]].map(([val, emoji, label]) => (
                <SelectCard
                  key={val}
                  emoji={emoji}
                  label={label}
                  selected={visibleStoreType.includes(val)}
                  onClick={() => {
                    const current = [...visibleStoreType];
                    const idx = current.indexOf(val);
                    if (idx >= 0) current.splice(idx, 1);
                    else current.push(val);
                    form.setFieldsValue({ storeType: current });
                  }}
                />
              ))}
            </div>
            <Form.Item shouldUpdate={() => true} style={{ marginBottom: 0 }}>
              {({ getFieldError }) => {
                const err = getFieldError("storeType");
                return err.length > 0 ? <div className={styles.fieldError} style={{ marginTop: 4 }}>{err[0]}</div> : null;
              }}
            </Form.Item>
          </div>
        </div>

        {/* Row 2: Categories \u2014 only shown when a type is selected */}
        {(visibleStoreType.includes("P") || visibleStoreType.includes("S")) && (
          <div className={styles.step1FieldRow} style={{ marginBottom: 0 }}>
            {visibleStoreType.includes("P") && (
              <div className={styles.step1FieldCol}>
                <div className={styles.fieldBlockLabel}>
                  <span className={styles.requiredAsterisk}>* </span>Product Category
                </div>
                <div className={styles.settingSubLabel} style={{ marginBottom: 6 }}>{BIZ.CATEGORY_SUBLABEL}</div>
                <Form.Item name="productSegment" rules={[{ required: true, message: "Please select a product category" }]} style={{ marginBottom: 0 }}>
                  <Select
                    showSearch
                    placeholder={BIZ.PRODUCT_CATEGORY_PLACEHOLDER}
                    optionFilterProp="label"
                    filterOption={filterCategoryOption}
                    options={productCategoryOptions}
                    data-testid="product-segment-select"
                    optionRender={(option) => {
                      const categoryId = option.value;
                      const categoryName = option.label;
                      return (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span>{categoryName}</span>
                          {hasProductTemplate(categoryId) && (
                            <span style={{ fontSize: 10, lineHeight: 1, padding: "2px 6px", borderRadius: 999, color: "#ad6800", background: "#fff7e6", border: "1px solid #ffd591" }}>
                              ⚡ Quick Setup
                            </span>
                          )}
                        </span>
                      );
                    }}
                  />
                </Form.Item>
              </div>
            )}
            {visibleStoreType.includes("S") && (
              <div className={styles.step1FieldCol}>
                <div className={styles.fieldBlockLabel}>
                  <span className={styles.requiredAsterisk}>* </span>Service Category
                </div>
                <div className={styles.settingSubLabel} style={{ marginBottom: 6 }}>{BIZ.CATEGORY_SUBLABEL}</div>
                <Form.Item name="serviceSegment" rules={[{ required: true, message: "Please select a service category" }]} style={{ marginBottom: 0 }}>
                  <Select
                    showSearch
                    placeholder={BIZ.SERVICE_CATEGORY_PLACEHOLDER}
                    optionFilterProp="label"
                    filterOption={filterCategoryOption}
                    options={serviceCategoryOptions}
                    data-testid="service-segment-select"
                    optionRender={(option) => {
                      const categoryId = option.value;
                      const categoryName = option.label;
                      return (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span>{categoryName}</span>
                          {hasServiceTemplate(categoryId) && (
                            <span style={{ fontSize: 10, lineHeight: 1, padding: "2px 6px", borderRadius: 999, color: "#ad6800", background: "#fff7e6", border: "1px solid #ffd591" }}>
                              ⚡ Quick Setup
                            </span>
                          )}
                        </span>
                      );
                    }}
                  />
                </Form.Item>
              </div>
            )}
          </div>
        )}
      </div>

      {/* \u2500\u2500 Card 2: Contact Details \u2500\u2500 */}
      <div className={styles.settingsGroup} style={{ marginBottom: 10 }}>
        <div className={styles.settingsGroupHeader}>
          <div className={styles.settingsGroupIconBox}>{"\ud83d\udcde"}</div>
          <div className={styles.settingsGroupInfo}>
            <div className={styles.settingsGroupTitle2}>{BIZ.CONTACT_CARD_TITLE}</div>
            <div className={styles.settingsGroupSubtitle}>{BIZ.CONTACT_CARD_SUBTITLE}</div>
          </div>
        </div>

        <div className={styles.fieldBlockLabel}>
          <span className={styles.requiredAsterisk}>* </span>{BIZ.CONTACT_LABEL}
        </div>
        <div className={styles.settingSubLabel} style={{ marginBottom: 8 }}>{BIZ.CONTACT_SUBLABEL}</div>

        <div className={`${styles.contactRow} ${styles.contactHalfWidth}`}>
          <div className={styles.contactPrefix}>
            <PrefixFormItem name="phonePrefix" disabled={contactNumberVerified || !contactEditMode} />
          </div>
          <Form.Item
            name="phoneDigits"
            noStyle
            rules={[
              { required: true, message: "Please enter your contact number" },
              { pattern: /^\d+$/, message: "Digits only" },
              { validator: (_, value) => {
                if (!value || !isMobileLengthValid(value)) return Promise.reject(new Error("Enter 6\u201314 digits"));
                return Promise.resolve();
              }},
            ]}
          >
            <Input
              className={styles.contactInput}
              disabled={contactNumberVerified || !contactEditMode}
              maxLength={MOBILE_MAX_LEN}
              placeholder="Contact number"
              onChange={(event) => {
                const next = String(event.target.value).replace(/\D/g, "").slice(0, MOBILE_MAX_LEN);
                form.setFieldsValue({ phoneDigits: next });
              }}
              bordered={false}
            />
          </Form.Item>
          {contactNumberVerified ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <div className={styles.verifiedPill}>\u2713 {BIZ.VERIFIED_LABEL}</div>
              <span
                className={styles.kycCtaLink}
                style={{ fontSize: 11 }}
                onClick={() => handleContactVerify("Edit")}
              >Edit</span>
            </div>
          ) : !contactEditMode ? (
            <Button
              className={styles.verifyBtn}
              onClick={() => setContactEditMode(true)}
            >
              Edit
            </Button>
          ) : (
            <Button
              className={styles.verifyBtn}
              onClick={() => handleContactVerify("")}
              disabled={contactVerifyLoading || contactResendLoading || !isMobileLengthValid(watchedPhoneDigits)}
              loading={contactVerifyLoading}
            >
              {contactVerifyLoading ? "Sending\u2026" : "Verify"}
            </Button>
          )}
        </div>

        {contactShowOtpInput && (
          <div className="inline-verification-block" style={{ margin: '0 0 10px 0' }}>
            <div className="inline-verification-block__otp">
              <span className="inline-verification-block__label">WhatsApp code</span>
              <Input.OTP length={6} value={contactOtp} onChange={setContactOtp} disabled={contactResendLoading} />
            </div>
            <Button
              type="default" size="small" className="inline-verification-block__resend"
              onClick={() => handleContactVerify("Resend")}
              disabled={contactResendCountdown > 0 || contactResendLoading}
              loading={contactResendLoading}
            >
              {contactResendCountdown > 0 ? `Resend in ${contactResendCountdown}s` : "Resend code"}
            </Button>
          </div>
        )}
        <div className={styles.fieldHint}>{BIZ.CONTACT_HINT}</div>
        <Form.Item name="phone" hidden><Input /></Form.Item>
      </div>

      {/* \u2500\u2500 Card 3: Business Location \u2500\u2500 */}
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupHeader}>
          <div className={styles.settingsGroupIconBox}>{"\ud83d\udccd"}</div>
          <div className={styles.settingsGroupInfo}>
            <div className={styles.settingsGroupTitle2}>{BIZ.LOCATION_CARD_TITLE}</div>
            <div className={styles.settingsGroupSubtitle}>{BIZ.LOCATION_CARD_SUBTITLE}</div>
          </div>
        </div>

        {/* Row 1: Address + Locality */}
        <div className={styles.step1FieldRow} style={{ marginBottom: 12 }}>
          <div className={styles.step1FieldCol}>
            <div className={styles.fieldBlockLabel}>
              <span className={styles.requiredAsterisk}>* </span>{BIZ.ADDRESS_LABEL}
            </div>
            <div className={styles.settingSubLabel} style={{ marginBottom: 6 }}>{BIZ.ADDRESS_SUBLABEL}</div>
            <Form.Item name="address" rules={[{ required: true, message: BIZ.ADDRESS_REQUIRED }, { max: ADDRESS_LINE_MAX, message: `Address must be ${ADDRESS_LINE_MAX} characters or less` }]} style={{ marginBottom: 0 }}>
              <Input.TextArea rows={3} placeholder={BIZ.ADDRESS_LABEL} maxLength={ADDRESS_LINE_MAX} showCount style={{ resize: "none" }} />
            </Form.Item>
            <Form.Item
              label={BIZ.LOCALITY_LABEL}
              name="locality"
              rules={[{ required: true, message: BIZ.LOCALITY_REQUIRED }, { max: ADDRESS_LINE_MAX, message: `Locality must be ${ADDRESS_LINE_MAX} characters or less` }]}
              style={{ marginTop: 8, marginBottom: 0 }}
            >
              <Input placeholder={BIZ.LOCALITY_PLACEHOLDER} maxLength={ADDRESS_LINE_MAX} />
            </Form.Item>
          </div>
        </div>

        {/* Row 2: Country / Zip / State / City */}
        <div className={styles.cityCountryRow} style={{ marginBottom: 12 }}>
          <div className={`${styles.cityCountryCol} ${styles.countryCol}`}>
            <Form.Item
              label={BIZ.COUNTRY_LABEL}
              name="country"
              initialValue="India"
              rules={[{ required: true, message: BIZ.COUNTRY_REQUIRED }]}
              style={{ marginBottom: 0 }}
            >
              <Select
                showSearch
                placeholder="Select country"
                optionFilterProp="label"
                options={countryOptionsForAddress}
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                popupMatchSelectWidth={false}
                labelRender={({ label }) => `${getFlagEmoji(String(label))} ${label}`}
                data-testid="country-select"
              />
            </Form.Item>
          </div>
          <div className={`${styles.cityCountryCol} ${styles.zipCol}`}>
            <Form.Item
              label={BIZ.ZIP_LABEL}
              name="zip"
              rules={[
                { required: true, message: BIZ.ZIP_REQUIRED },
                { pattern: /^[0-9]{5,6}$/, message: BIZ.ZIP_PATTERN },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="5 or 6 digits" maxLength={6} />
            </Form.Item>
          </div>
          <div className={`${styles.cityCountryCol} ${styles.stateCol}`}>
            <Form.Item
              label={BIZ.STATE_LABEL}
              name="state"
              rules={[{ required: true, message: BIZ.STATE_REQUIRED }]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder={BIZ.STATE_LABEL} />
            </Form.Item>
          </div>
          <div className={`${styles.cityCountryCol} ${styles.cityCol}`}>
            <Form.Item
              label={BIZ.CITY_LABEL}
              name="city"
              rules={[{ required: true, message: BIZ.CITY_REQUIRED }]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder={BIZ.CITY_LABEL} />
            </Form.Item>
          </div>
        </div>
        {(zipLookupLoading || zipLookupFailed) && (
          <div className={styles.fieldHint} style={{ marginBottom: 12 }}>
            {zipLookupLoading
              ? "Looking up city/state from ZIP..."
              : "Could not auto-detect city/state from ZIP. Please enter manually."}
          </div>
        )}

        {/* Map URL */}
        <div className={styles.fieldBlockLabel}>Google Maps Link</div>
        <div className={styles.settingSubLabel} style={{ marginBottom: 6 }}>Shareable URL of your business location on Google Maps</div>
        <div style={{ display: "flex", gap: 0, width: "100%", alignItems: "stretch" }}>
          <Form.Item name="mapUrl" noStyle>
            <Input placeholder={BIZ.MAP_LOCATION_PLACEHOLDER} style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }} />
          </Form.Item>
          <Tooltip title={BIZ.MAP_OPEN_BUTTON}>
            <Button
              icon={<GlobalOutlined />}
              style={{ height: "auto", borderTopLeftRadius: 0, borderBottomLeftRadius: 0, lineHeight: 1 }}
              onClick={() => {
                const existingMapUrl = watchedValues?.mapUrl || form.getFieldValue("mapUrl");
                if (existingMapUrl) {
                  window.open(existingMapUrl, "_blank", "noopener");
                  return;
                }
                const parts = [watchedZip, watchedCountry].filter(Boolean);
                const url = parts.length
                  ? `https://www.google.com/maps/place/${encodeURIComponent(parts.join(", "))}`
                  : "https://maps.google.com";
                window.open(url, "_blank", "noopener");
              }}
            />
          </Tooltip>
        </div>
        <div className={styles.fieldHint} style={{ marginTop: 4 }}>{BIZ.MAP_LOCATION_HINT}</div>

        {/* Geo Location */}
        <div className={styles.fieldBlockLabel} style={{ marginTop: 16 }}>{BIZ.MAP_LOCATION_LABEL}</div>
        <div className={styles.settingSubLabel} style={{ marginBottom: 6 }}>{BIZ.MAP_LOCATION_SUBLABEL}</div>
        <LocationPicker
          value={(() => {
            return normalizeGeocode(watchedGeocode);
          })()}
          address={locationPickerAddress}
          onChange={(geo) => form.setFieldsValue({ geocode: JSON.stringify(geo) })}
        />
        <Form.Item name="geocode" hidden>
          <Input />
        </Form.Item>
      </div>

      <div style={{ height: 44 }} />
    </>
  );

  const toggleDeliveryOption = (option) => {
    const current = form.getFieldValue("deliveryOptions") || ["PICKUP"];
    const next = current.includes(option)
      ? current.filter(v => v !== option)
      : [...current, option];
    if (next.length > 0) form.setFieldsValue({ deliveryOptions: next });
  };

  const togglePaymentMethod = (method) => {
    if (method === "GATEWAY" && !kycVerified) return;
    const current = form.getFieldValue("paymentMethods") || ["SELF_MANAGED"];
    const next = current.includes(method)
      ? current.filter(v => v !== method)
      : [...current, method];
    if (next.length > 0) form.setFieldsValue({ paymentMethods: next });
  };

  const renderStep2 = () => (
    <>
      <div className={styles.pageHeader}>{BIZ.PAGE_TITLE}</div>
      {renderProgressBar()}

      <div className={styles.settingsGrid}>

      {/* Card 1 — Business Operations */}
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupHeader}>
          <div className={styles.settingsGroupIconBox}>🏪</div>
          <div className={styles.settingsGroupInfo}>
            <div className={styles.settingsGroupTitle2}>{BIZ.OPERATIONS_CARD_TITLE}</div>
            <div className={styles.settingsGroupSubtitle}>{BIZ.OPERATIONS_CARD_SUBTITLE}</div>
          </div>
        </div>

        {/* ── Business Status ── */}
        <div className={styles.sectionDivider}>
          <div className={styles.sectionDividerText}>{BIZ.SECTION_BUSINESS_STATUS}</div>
          <div className={styles.sectionDividerLine} />
        </div>

        {/* Visibility */}
        <div className={styles.settingRow}>
          <div className={styles.settingLabelGroup}>
            <div className={styles.settingLabel}>{BIZ.VISIBILITY_LABEL}</div>
            <div className={styles.settingSubLabel}>{BIZ.VISIBILITY_SUBLABEL}</div>
          </div>
          <div className={styles.segGroup}>
            <div
              className={`${styles.segBtn} ${!watchedBizPrivate ? styles.segBtnSelected : styles.segBtnUnselected}`}
              onClick={() => form.setFieldsValue({ bizPrivate: false })}
            >{BIZ.VISIBILITY_PUBLIC}</div>
            <Tooltip
              title={privateStoreAllowed !== true ? CATALOG_LIMIT_LABELS.privateStoreTitle : undefined}
              placement="top"
            >
              <span style={{ display: "inline-block" }}>
                <div
                  className={`${styles.segBtn} ${watchedBizPrivate ? styles.segBtnSelected : styles.segBtnUnselected} ${privateStoreAllowed !== true ? styles.segBtnDisabled : ""}`}
                  onClick={() => { if (privateStoreAllowed) form.setFieldsValue({ bizPrivate: true }); }}
                >{BIZ.VISIBILITY_PRIVATE}</div>
              </span>
            </Tooltip>
          </div>
        </div>
        <Form.Item name="bizPrivate" hidden initialValue={false}><Input /></Form.Item>

        {watchedBizPrivate && (
          <>
            <Form.Item label={BIZ.PASSKEY_LABEL} name="bizPasskey"
              extra={mode === "edit" && privateEditUnlocked ? "Leave blank to keep the existing passkey. Enter a new value to change it." : undefined}
              rules={mode === "edit" && privateEditUnlocked ? [{ min: 5, message: "Passkey must be at least 5 characters if you enter one" }] : [{ required: true, message: "Passkey is required for private Biz" }, { min: 5, message: "Passkey must be at least 5 characters" }]}
            >
              <Input.Password placeholder={BIZ.PASSKEY_PLACEHOLDER} />
            </Form.Item>
            <Form.Item label={BIZ.CONFIRM_PASSKEY_LABEL} name="confirmPasskey" dependencies={["bizPasskey"]}
              rules={[
                ...(mode === "edit" && privateEditUnlocked ? [] : [{ required: true, message: "Please confirm your passkey" }]),
                ({ getFieldValue }) => ({ validator(_, value) { const p = getFieldValue("bizPasskey"); if (!p && !value) return Promise.resolve(); if (p === value) return Promise.resolve(); return Promise.reject(new Error("Passkeys do not match!")); } }),
              ]}
            >
              <Input.Password placeholder={BIZ.CONFIRM_PASSKEY_PLACEHOLDER} />
            </Form.Item>
          </>
        )}

        {/* Open status */}
        <div className={styles.settingRow}>
          <div className={styles.settingLabelGroup}>
            <div className={styles.settingLabel}>{BIZ.OPEN_STATUS_LABEL}</div>
            <div className={styles.settingSubLabel}>{BIZ.OPEN_STATUS_SUBLABEL}</div>
          </div>
          <div className={styles.segGroup}>
            <div
              className={`${styles.segBtn} ${(watchedBizOpenStatus ?? true) ? styles.segBtnSelected : styles.segBtnUnselected}`}
              onClick={() => form.setFieldsValue({ bizOpenStatus: true })}
            >{BIZ.OPEN}</div>
            <div
              className={`${styles.segBtn} ${!(watchedBizOpenStatus ?? true) ? styles.segBtnSelected : styles.segBtnUnselected}`}
              onClick={() => form.setFieldsValue({ bizOpenStatus: false })}
            >{BIZ.CLOSED}</div>
          </div>
          <Form.Item name="bizOpenStatus" initialValue={true} hidden><Input /></Form.Item>
        </div>

        {/* Customer listing */}
        <div className={styles.settingRow}>
          <div className={styles.settingLabelGroup}>
            <div className={styles.settingLabel}>{BIZ.LISTING_LABEL}</div>
            <div className={styles.settingSubLabel}>{BIZ.LISTING_SUBLABEL}</div>
          </div>
          <div className={styles.segGroup}>
            <div
              className={`${styles.segBtn} ${(watchedBizLive ?? false) ? styles.segBtnSelected : styles.segBtnUnselected}`}
              onClick={markBizLive}
            >{BIZ.LIVE}</div>
            <div
              className={`${styles.segBtn} ${!(watchedBizLive ?? false) ? styles.segBtnSelected : styles.segBtnUnselected}`}
              onClick={() => form.setFieldsValue({ bizLive: false })}
            >{BIZ.DRAFT}</div>
          </div>
          <Form.Item name="bizLive" initialValue={mode === "edit" ? undefined : false} hidden><Input /></Form.Item>
        </div>

        {/* ── Service Coverage ── */}
        <div className={styles.sectionDivider}>
          <div className={styles.sectionDividerText}>{BIZ.SECTION_COVERAGE_AREA}</div>
          <div className={styles.sectionDividerLine} />
        </div>
        <div className={styles.settingsGroupSubtitle} style={{ margin: "-4px 0 12px" }}>
          {BIZ.SERVICE_COVERAGE_HELPTEXT}
        </div>

        <div className={styles.fieldBlock}>
          <div className={styles.fieldBlockLabel}>{BIZ.COVERAGE_SCOPE_LABEL}</div>
          <div className={styles.optionGroup}>
            <OverflowTooltip title={BIZ.SERVICE_COVERAGE_SCOPE_ANYWHERE}>
              <SelectCard
                label={BIZ.SERVICE_COVERAGE_SCOPE_ANYWHERE}
                selected={!watchedServiceCoverageScope || watchedServiceCoverageScope === "ANYWHERE"}
                onClick={() => form.setFieldsValue({ serviceCoverageScope: "ANYWHERE" })}
              />
            </OverflowTooltip>
            <OverflowTooltip title={BIZ.SERVICE_COVERAGE_SCOPE_CITY}>
              <SelectCard
                label={BIZ.SERVICE_COVERAGE_SCOPE_CITY}
                selected={watchedServiceCoverageScope === "ENTIRE_CITY"}
                onClick={() => form.setFieldsValue({ serviceCoverageScope: "ENTIRE_CITY" })}
              />
            </OverflowTooltip>
            <OverflowTooltip title={BIZ.SERVICE_COVERAGE_SCOPE_SELECTED_AREAS}>
              <SelectCard
                label={BIZ.SERVICE_COVERAGE_SCOPE_SELECTED_AREAS}
                selected={watchedServiceCoverageScope === "SELECTED_AREAS"}
                onClick={() => form.setFieldsValue({ serviceCoverageScope: "SELECTED_AREAS" })}
              />
            </OverflowTooltip>
          </div>
          <Form.Item name="serviceCoverageScope" hidden initialValue="ANYWHERE"><Input /></Form.Item>
          {watchedServiceCoverageScope === "SELECTED_AREAS" && (
            <div style={{ marginTop: 8 }}>
              <Input
                placeholder={BIZ.SERVICE_AREAS_PLACEHOLDER}
                value={areaInput}
                onChange={(e) => setAreaInput(e.target.value)}
                onPressEnter={() => {
                  const val = areaInput.trim();
                  const current = form.getFieldValue("serviceCoverageAreas") || [];
                  if (!val) return;
                  if (current.includes(val)) return;
                  if (current.length >= MAX_SERVICE_AREAS) return;
                  form.setFieldsValue({ serviceCoverageAreas: [...current, val] });
                  setAreaInput("");
                }}
                disabled={watchedServiceCoverageAreas.length >= MAX_SERVICE_AREAS}
              />
              <div style={{ marginTop: 4, marginBottom: 8, fontSize: 11, color: "#888" }}>
                {BIZ.SERVICE_AREAS_SUBTEXT}
              </div>
              {watchedServiceCoverageAreas.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {watchedServiceCoverageAreas.map((area, i) => (
                    <Tag
                      key={i}
                      closable
                      onClose={() => {
                        const next = watchedServiceCoverageAreas.filter((_, j) => j !== i);
                        form.setFieldsValue({ serviceCoverageAreas: next });
                      }}
                    >
                      {area}
                    </Tag>
                  ))}
                </div>
            )}
          </div>
        )}
          <Form.Item name="serviceCoverageAreas" hidden><Input /></Form.Item>
        </div>

        {/* ── Pre-Order / Pre-Booking ── */}
        <div className={styles.sectionDivider}>
          <div className={styles.sectionDividerText}>{BIZ.SECTION_PREBOOKING}</div>
          <div className={styles.sectionDividerLine} />
        </div>

        <Form.Item name="prebookingGroups" hidden initialValue="[]"><Input /></Form.Item>

        {/* ── Availability Calendar ── */}
        <div className={styles.calendarSection}>
          <div className={`${styles.fieldBlockLabel} ${styles.calendarTitle}`}>{BIZ.PREBOOKING_CALENDAR_TITLE}</div>
          <Calendar
            fullscreen={false}
            headerRender={({ value, onChange }) => (
              <div className={styles.calendarHeader}>
                <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => onChange(value.subtract(1, "month"))} />
                <span className={styles.calendarMonthText}>{value.format("MMMM YYYY")}</span>
                <Button type="text" size="small" icon={<RightOutlined />} onClick={() => onChange(value.add(1, "month"))} />
                <Button type="link" size="small" className={styles.calendarTodayBtn} onClick={() => onChange(dayjs())}>Today</Button>
              </div>
            )}
            cellRender={(date) => {
              const key = date.format("YYYY-MM-DD");
              const gis = dateGroupMap[key] || [];
              if (gis.length > 0) {
                return (
                  <div style={{ textAlign: "center", display: "flex", justifyContent: "center", gap: 2 }}>
                    {gis.map(gi => <Badge key={gi} dot color={GROUP_COLORS[gi % GROUP_COLORS.length]} />)}
                  </div>
                );
              }
              return null;
            }}
            onSelect={(date) => setCalendarSelectedDate(date)}
          />
          {calendarSelectedDate && (() => {
            const key = calendarSelectedDate.format("YYYY-MM-DD");
            const gis = dateGroupMap[key] || [];
            return (
              <div className={styles.calendarPreviewCard}>
                <div className={styles.calendarPreviewTitle}>
                  {calendarSelectedDate.format("ddd, MMM D, YYYY")}
                  {gis.length > 0 && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                      {gis.map(gi => (
                        <span key={gi} className={styles.calendarPreviewGroupLabel} style={{ color: GROUP_COLORS[gi % GROUP_COLORS.length] }}>
                          {BIZ.PREBOOKING_GROUP_LABEL} {gi + 1}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {gis.length === 0 ? (
                  <div className={styles.calendarPreviewEmpty}>{BIZ.PREBOOKING_NO_SLOTS}</div>
                ) : (
                  <div className={styles.calendarPreviewSlots}>
                    {gis.map(gi => (
                      <div key={gi} style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: GROUP_COLORS[gi % GROUP_COLORS.length] }}>
                          {BIZ.PREBOOKING_GROUP_LABEL} {gi + 1}
                        </div>
                        {prebookingGroups[gi]?.slots.length === 0 ? (
                          <Tag>{BIZ.PREBOOKING_NO_SLOTS}</Tag>
                        ) : prebookingGroups[gi]?.slots.map((slot, i) => {
                          const slotLabel = slotDisplayValue(slot);
                          return slotLabel ? <Tag key={i}>{slotLabel}</Tag> : null;
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          <div className={styles.calendarHint}>
            Tap a date to see its slots
          </div>
        </div>

        {prebookingGroups.length === 0 && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => {
              const g = { key: Date.now() + Math.random(), datePickerValue: [], slots: [], duration: 60, breakDuration: 0, maxOrdersPerSlot: null };
              setPrebookingGroups([g]);
              setExpandedGroupKey(g.key);
              form.setFieldsValue({ prebookingGroups: JSON.stringify([{ dates: [], slots: [], duration: 60, breakDuration: 0, maxOrdersPerSlot: null }]) });
            }}
            style={{ width: "100%", marginBottom: 16 }}
          >
            {BIZ.PREBOOKING_ADD_GROUP}
          </Button>
        )}

          {prebookingGroups.map((group, gi) => (
          <div
            key={group.key}
            className={styles.groupCard}
            style={{ borderLeft: `4px solid ${GROUP_COLORS[gi % GROUP_COLORS.length]}` }}
          >
            <div
              onClick={() => setExpandedGroupKey(expandedGroupKey === group.key ? null : group.key)}
              className={styles.groupHeader}
            >
              <div className={styles.groupTitleRow}>
                <div className={styles.groupTitleLeft}>
                  <span className={expandedGroupKey === group.key ? styles.groupCollapseArrowExpanded : styles.groupCollapseArrowCollapsed}>▼</span>
                  <span className={`${styles.fieldBlockLabel} ${styles.groupTitleLabel}`}>
                    {BIZ.PREBOOKING_GROUP_LABEL} {gi + 1}
                  </span>
                </div>
                <Button
                  type="text"
                  size="small"
                  className={styles.groupRemoveBtn}
                  icon={<PlusOutlined style={{ transform: "rotate(45deg)" }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    const updated = prebookingGroups.filter(g => g.key !== group.key);
                    setPrebookingGroups(updated);
                    if (expandedGroupKey === group.key) setExpandedGroupKey(updated.length > 0 ? updated[0].key : null);
                    form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot })))
                    });
                  }}
                />
              </div>
              <div className={styles.groupStats}>
                <span className={styles.groupStatItem}>
                  <CalendarOutlined className={styles.groupStatIcon} />
                  <span>{group.datePickerValue.length} Date{group.datePickerValue.length !== 1 ? "s" : ""}</span>
                </span>
	                <span className={styles.groupStatItem}>
	                  <ClockCircleOutlined className={styles.groupStatIcon} />
	                  <span>{group.slots.length > 0 ? `${group.slots.length} Slot${group.slots.length !== 1 ? "s" : ""}` : BIZ.PREBOOKING_NO_SLOTS}</span>
	                </span>
              </div>
            </div>

            {expandedGroupKey === group.key && (
              <div className={styles.groupContent}>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <div className={`${styles.fieldBlockLabel} ${styles.groupFieldLabel}`}>Slot Duration</div>
                      <Select
                        value={group.duration || 60}
                        onChange={(val) => {
                          const updated = prebookingGroups.map(g =>
                            g.key === group.key ? { ...g, duration: val, slots: [] } : g
                          );
                          setPrebookingGroups(updated);
                          form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
                        }}
                        style={{ width: "100%" }}
                        options={DURATION_OPTIONS}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className={`${styles.fieldBlockLabel} ${styles.groupFieldLabel}`}>Break Duration</div>
                      <Select
                        value={group.breakDuration ?? 0}
                        onChange={(val) => {
                          const updated = prebookingGroups.map(g =>
                            g.key === group.key ? { ...g, breakDuration: val, slots: [] } : g
                          );
                          setPrebookingGroups(updated);
                          form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
                        }}
                        style={{ width: "100%" }}
                        options={BREAK_DURATION_OPTIONS}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div className={`${styles.fieldBlockLabel} ${styles.groupFieldLabel}`}>{BIZ.PREBOOKING_MAX_ORDERS}</div>
                  <InputNumber
                    value={group.maxOrdersPerSlot ?? null}
                    onChange={(val) => {
                      const updated = prebookingGroups.map(g =>
                        g.key === group.key ? { ...g, maxOrdersPerSlot: val ?? null } : g
                      );
                      setPrebookingGroups(updated);
                      form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
                    }}
                    min={1}
                    max={999}
                    placeholder="Unlimited"
                    style={{ width: "100%" }}
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div className={`${styles.fieldBlockLabel} ${styles.groupFieldLabel}`}>{BIZ.PREBOOKING_DATES_LABEL}</div>
                  <Popover
                    trigger="click"
                    open={datePopoverKey === group.key}
                    onOpenChange={(v) => {
                      setDatePopoverKey(v ? group.key : null);
                      if (v) setDatePickerMonth(dayjs().startOf("month"));
                    }}
                    content={<MonthPickerPanel
                      datePickerMonth={datePickerMonth}
                      setDatePickerMonth={setDatePickerMonth}
                      group={group}
                      gi={gi}
                      prebookingGroups={prebookingGroups}
                      setPrebookingGroups={setPrebookingGroups}
                      form={form}
                      dateGroupMap={dateGroupMap}
                      GROUP_COLORS={GROUP_COLORS}
                    />}
                  >
                    <Button type="dashed" icon={<PlusOutlined />} block>Select Dates</Button>
                  </Popover>
                  {group.datePickerValue.length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {(() => {
                        const sorted = [...group.datePickerValue].sort((a, b) => a.valueOf() - b.valueOf());
                        const ranges = [];
                        let start = sorted[0];
                        let prev = sorted[0];
                        for (let i = 1; i < sorted.length; i++) {
                          if (sorted[i].diff(prev, "day") === 1) {
                            prev = sorted[i];
                          } else {
                            ranges.push({ start, end: prev });
                            start = sorted[i];
                            prev = sorted[i];
                          }
                        }
                        ranges.push({ start, end: prev });
                        return ranges.map((r, ri) => (
                          <Tag
                            key={ri}
                            closable
                            onClose={() => {
                              const removeStart = r.start.valueOf();
                              const removeEnd = r.end.valueOf();
                              const kept = group.datePickerValue.filter(d => {
                                const v = d.valueOf();
                                return v < removeStart || v > removeEnd;
                              });
                              const updated = prebookingGroups.map(g =>
                                g.key === group.key ? { ...g, datePickerValue: kept } : g
                              );
                              setPrebookingGroups(updated);
                              form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
                            }}
                          >
                            {r.start.isSame(r.end, "day")
                              ? r.start.format("MMM D")
                              : `${r.start.format("MMM D")} – ${r.end.format("D")}`}
                          </Tag>
                        ));
                      })()}
                    </div>
                  )}
                </div>

                <div>
                  <div className={`${styles.fieldBlockLabel} ${styles.groupFieldLabel}`}>{BIZ.PREBOOKING_SLOTS_LABEL}</div>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    block
                    onClick={() => {
                      setPendingSlots([...group.slots]);
                      setSlotPopoverKey(group.key);
                    }}
                  >
                    {group.slots.length > 0 ? "Edit Slots" : "Select Slots"}
                  </Button>
                  <Modal
                    title="Select Time Slots"
                    open={slotPopoverKey === group.key}
                    onCancel={() => setSlotPopoverKey(null)}
                    onOk={() => {
                      const updated = prebookingGroups.map(g =>
                        g.key === group.key ? { ...g, slots: pendingSlots } : g
                      );
                      setPrebookingGroups(updated);
                      form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
                      setSlotPopoverKey(null);
                    }}
                  >
                    {(() => {
                      const allSlots = generateTimeSlots(group.duration || 60, group.breakDuration || 0);
                      return (
                        <div style={{ maxHeight: 320, overflowY: "auto" }}>
                          {allSlots.map((slot, si) => {
                            const checked = pendingSlots.some(s => slotDisplayValue(s) === slotDisplayValue(slot));
                            return (
                              <div
                                key={si}
                                style={{ padding: "4px 0", cursor: "pointer" }}
                                onClick={() => {
                                  setPendingSlots(prev =>
                                    checked
                                      ? prev.filter(s => slotDisplayValue(s) !== slotDisplayValue(slot))
                                      : [...prev, slot]
                                  );
                                }}
                              >
                                <Checkbox checked={checked}>
                                  {slot.start} — {slot.end}
                                </Checkbox>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </Modal>
	                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
	                    {group.slots.length === 0 ? (
	                      <Tag>{BIZ.PREBOOKING_NO_SLOTS}</Tag>
	                    ) : (
	                      group.slots.map((slot, si) => (
	                        <Tag
	                          key={si}
                          closable
                          onClose={() => {
                            const newSlots = group.slots.filter((_, idx) => idx !== si);
                            const updated = prebookingGroups.map(g =>
                              g.key === group.key ? { ...g, slots: newSlots } : g
                            );
                            setPrebookingGroups(updated);
                            form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
                          }}
	                        >
	                          {slotDisplayValue(slot)}
	                        </Tag>
	                      ))
	                    )}
	                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {prebookingGroups.length > 0 && (
          <div style={{ marginTop: 8, marginBottom: 16 }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                const g = { key: Date.now() + Math.random(), datePickerValue: [], slots: [], duration: 60, breakDuration: 0, maxOrdersPerSlot: null };
                const updated = [...prebookingGroups, g];
                setPrebookingGroups(updated);
                setExpandedGroupKey(g.key);
                form.setFieldsValue({ prebookingGroups: JSON.stringify(updated.map(g => ({ dates: g.datePickerValue.map(d => d.format("YYYY-MM-DD")), slots: g.slots, duration: g.duration, breakDuration: g.breakDuration, maxOrdersPerSlot: g.maxOrdersPerSlot }))) });
              }}
              style={{ width: "100%" }}
            >
              {BIZ.PREBOOKING_ADD_GROUP}
            </Button>
          </div>
        )}

      </div>

      {/* Card 2 — Customer experience: Fulfillment → Payments → Promotion */}
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupHeader}>
          <div className={styles.settingsGroupIconBox}>✨</div>
          <div className={styles.settingsGroupInfo}>
            <div className={styles.settingsGroupTitle2}>{BIZ.EXPERIENCE_CARD_TITLE}</div>
            <div className={styles.settingsGroupSubtitle}>{BIZ.EXPERIENCE_CARD_SUBTITLE}</div>
          </div>
        </div>

        {/* ── Fulfillment ── */}
        <div className={styles.sectionDivider}>
          <div className={styles.sectionDividerText}>{BIZ.SECTION_FULFILLMENT}</div>
          <div className={styles.sectionDividerLine} />
        </div>

        <div className={styles.fieldBlock}>
          <div className={styles.fieldBlockLabel}>{BIZ.DELIVERY_LABEL}</div>
          <div className={styles.fieldBlockHint}>{BIZ.DELIVERY_SUBLABEL}</div>
          <div className={styles.optionGroup}>
            <SelectCard
              label={BIZ.DELIVERY_OPTION}
              selected={watchedDeliveryOptions?.includes("DELIVERY")}
              onClick={() => toggleDeliveryOption("DELIVERY")}
            />
            <SelectCard
              label={BIZ.PICKUP_OPTION}
              selected={watchedDeliveryOptions?.includes("PICKUP")}
              onClick={() => toggleDeliveryOption("PICKUP")}
            />
          </div>
          <Form.Item name="deliveryOptions" initialValue={["PICKUP"]} hidden><Input /></Form.Item>
        </div>

        {/* ── Payments ── */}
        <div className={styles.sectionDivider}>
          <div className={styles.sectionDividerText}>{BIZ.SECTION_PAYMENTS}</div>
          <div className={styles.sectionDividerLine} />
        </div>

        <div className={styles.fieldBlock}>
          <div className={styles.fieldBlockLabel}>{BIZ.PAYMENT_LABEL}</div>
          <div className={styles.fieldBlockHint}>{BIZ.PAYMENT_SUBLABEL}</div>
          <div className={styles.optionGroup}>
            <SelectCard
              label={BIZ.PAYMENT_SELF}
              selected={watchedPaymentMethods?.includes("SELF_MANAGED")}
              onClick={() => togglePaymentMethod("SELF_MANAGED")}
            />
            <Tooltip title={!kycVerified ? BIZ.PAYMENT_GATEWAY_KYC_HINT : undefined} placement="top">
              <span style={{ flex: 1, minWidth: 90, display: "flex" }}>
                <SelectCard
                  label={BIZ.PAYMENT_GATEWAY}
                  selected={kycVerified && watchedPaymentMethods?.includes("GATEWAY")}
                  disabled={!kycVerified}
                  onClick={() => togglePaymentMethod("GATEWAY")}
                  style={{ width: "100%" }}
                />
              </span>
            </Tooltip>
          </div>
          {!kycVerified && (
            <div className={styles.kycWarningBox}>
              <span className={styles.kycWarningIcon}>⚠️</span>
              <div style={{ fontSize: 11, color: "#b45309", lineHeight: 1.6 }}>
                <div>{BIZ.PAYMENT_GATEWAY_KYC_HINT}</div>
                <span className={styles.kycCtaLink} onClick={() => navigate("/hubSettings/kyc")}>Start KYC →</span>
              </div>
            </div>
          )}
          <Form.Item name="paymentMethods" initialValue={["SELF_MANAGED"]} hidden><Input /></Form.Item>
        </div>

        {/* ── Promotion ── */}
        <div className={styles.sectionDivider}>
          <div className={styles.sectionDividerText}>{BIZ.SECTION_PROMOTION}</div>
          <div className={styles.sectionDividerLine} />
        </div>

        {/* Flash Message */}
        <div className={styles.fieldBlock}>
          <div className={styles.fieldBlockLabel}>{BIZ.FLASH_LABEL}</div>
          <div className={styles.fieldBlockHint}>{BIZ.FLASH_SUBLABEL}</div>
          <div className={styles.segGroup}>
            <div
              className={`${styles.segBtn} ${watchedFlashEnabled ? styles.segBtnSelected : styles.segBtnUnselected}`}
              onClick={() => form.setFieldsValue({ flashEnabled: true })}
            >{BIZ.FLASH_YES}</div>
            <div
              className={`${styles.segBtn} ${!watchedFlashEnabled ? styles.segBtnSelected : styles.segBtnUnselected}`}
              onClick={() => form.setFieldsValue({ flashEnabled: false })}
            >{BIZ.FLASH_NO}</div>
          </div>
          <Form.Item name="flashEnabled" initialValue={false} hidden><Input /></Form.Item>
        </div>
        {watchedFlashEnabled && (
          <Form.Item name="flashMessage" rules={[{ required: true, message: BIZ.FLASH_TEXT_REQUIRED }]} style={{ marginBottom: 14 }}>
            <Input.TextArea rows={2} placeholder={BIZ.FLASH_TEXT_PLACEHOLDER} maxLength={120} showCount />
          </Form.Item>
        )}

        {/* Special Discount */}
        <div className={styles.fieldBlock} style={{ marginBottom: 0 }}>
          <div className={styles.fieldBlockLabel}>{BIZ.DISCOUNT_LABEL}</div>
          <div className={styles.fieldBlockHint}>{BIZ.DISCOUNT_SUBLABEL}</div>
          <Form.Item name="specialDiscount" style={{ marginBottom: 0 }}>
            <Input
              type="number"
              min={0}
              max={90}
              placeholder="0"
              addonAfter="%"
              size="large"
              className={styles.discountInput}
            />
          </Form.Item>
        </div>
      </div>

      {/* Card 3 — Storefront settings */}
      <div className={styles.settingsGroup}>
        <div className={styles.settingsGroupHeader}>
          <div className={styles.settingsGroupIconBox}>🖼️</div>
          <div className={styles.settingsGroupInfo}>
            <div className={styles.settingsGroupTitle2}>{BIZ.STOREFRONT_CARD_TITLE}</div>
            <div className={styles.settingsGroupSubtitle}>{BIZ.STOREFRONT_CARD_SUBTITLE}</div>
          </div>
        </div>

        {/* Display image — needs bizId for S3 path; show unlock hint on first create pass */}
        <div className={styles.fieldBlock}>
          <div className={styles.fieldBlockLabel}>{BIZ.DISPLAY_IMAGE_LABEL}</div>
          <div className={styles.fieldBlockHint}>{BIZ.DISPLAY_IMAGE_SUBLABEL}</div>
          {effectiveBizId ? (
            <Form.Item name="bizImageKey" style={{ marginBottom: 0 }}>
              <BizImageUpload
                token={token}
                hubId={effectiveHubId}
                bizId={effectiveBizId}
              />
            </Form.Item>
          ) : (
            <div className={styles.fieldBlockHint}>{BIZ.DISPLAY_IMAGE_SAVE_FIRST}</div>
          )}
        </div>

        {/* Social Footprints — above Business docs */}
        <div className={styles.fieldBlock}>
          <div className={styles.fieldBlockLabel}>{BIZ.SOCIAL_FOOTPRINTS_LABEL}</div>
          <div className={styles.fieldBlockHint}>{BIZ.SOCIAL_FOOTPRINTS_SUBLABEL}</div>
          <Form.Item
            name={["socialFootprints", "youtube"]}
            label={BIZ.SOCIAL_YOUTUBE}
            rules={[{
              validator: (_, value) => {
                const trimmed = String(value || "").trim();
                if (!trimmed) return Promise.resolve();
                if (/^https?:\/\//i.test(trimmed)) return Promise.resolve();
                return Promise.reject(new Error(BIZ.SOCIAL_URL_INVALID));
              },
            }]}
            style={{ marginBottom: 8 }}
          >
            <Input placeholder="https://youtube.com/..." />
          </Form.Item>
          <Form.Item
            name={["socialFootprints", "instagram"]}
            label={BIZ.SOCIAL_INSTAGRAM}
            rules={[{
              validator: (_, value) => {
                const trimmed = String(value || "").trim();
                if (!trimmed) return Promise.resolve();
                if (/^https?:\/\//i.test(trimmed)) return Promise.resolve();
                return Promise.reject(new Error(BIZ.SOCIAL_URL_INVALID));
              },
            }]}
            style={{ marginBottom: 8 }}
          >
            <Input placeholder="https://instagram.com/..." />
          </Form.Item>
          <Form.Item
            name={["socialFootprints", "facebook"]}
            label={BIZ.SOCIAL_FACEBOOK}
            rules={[{
              validator: (_, value) => {
                const trimmed = String(value || "").trim();
                if (!trimmed) return Promise.resolve();
                if (/^https?:\/\//i.test(trimmed)) return Promise.resolve();
                return Promise.reject(new Error(BIZ.SOCIAL_URL_INVALID));
              },
            }]}
            style={{ marginBottom: 8 }}
          >
            <Input placeholder="https://facebook.com/..." />
          </Form.Item>
          <Form.Item
            name={["socialFootprints", "linkedin"]}
            label={BIZ.SOCIAL_LINKEDIN}
            rules={[{
              validator: (_, value) => {
                const trimmed = String(value || "").trim();
                if (!trimmed) return Promise.resolve();
                if (/^https?:\/\//i.test(trimmed)) return Promise.resolve();
                return Promise.reject(new Error(BIZ.SOCIAL_URL_INVALID));
              },
            }]}
            style={{ marginBottom: 8 }}
          >
            <Input placeholder="https://linkedin.com/..." />
          </Form.Item>
          <Form.Item
            name={["socialFootprints", "x"]}
            label={BIZ.SOCIAL_X}
            rules={[{
              validator: (_, value) => {
                const trimmed = String(value || "").trim();
                if (!trimmed) return Promise.resolve();
                if (/^https?:\/\//i.test(trimmed)) return Promise.resolve();
                return Promise.reject(new Error(BIZ.SOCIAL_URL_INVALID));
              },
            }]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder="https://x.com/..." />
          </Form.Item>
        </div>

        {/* Business docs */}
        <div className={styles.fieldBlock}>
          <div className={styles.fieldBlockLabel}>{BUSINESS_DOCS_LABELS.BIZ_MAPPING_TITLE}</div>
          <div className={styles.fieldBlockHint}>{BIZ.BUSINESS_DOCS_SUBLABEL}</div>
          <Form.List name="businessDocs">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  const selectedDocId = watchedBusinessDocs?.[name]?.docId;
                  const selectedDoc = availableBusinessDocs.find((doc) => doc.docId === selectedDocId);
                  return (
                    <div key={key} className={styles.docMappingRow}>
                      <Form.Item
                        {...restField}
                        name={[name, "label"]}
                        rules={[{ required: true, message: BUSINESS_DOCS_LABELS.BIZ_MAPPING_LABEL_REQUIRED }]}
                        className={styles.docMappingLabel}
                      >
                        <Input placeholder={BUSINESS_DOCS_LABELS.BIZ_MAPPING_LABEL} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "docId"]}
                        rules={[{ required: true, message: BUSINESS_DOCS_LABELS.BIZ_MAPPING_DOC_REQUIRED }]}
                        className={styles.docMappingSelect}
                      >
                        <Select
                          placeholder={BUSINESS_DOCS_LABELS.BIZ_MAPPING_DOC}
                          onChange={(docId) => {
                            const selectedDocForMapping = availableBusinessDocs.find((doc) => doc.docId === docId);
                            const currentMappings = [...(form.getFieldValue("businessDocs") || [])];
                            currentMappings[name] = {
                              ...(currentMappings[name] || {}),
                              docId,
                              contentType: selectedDocForMapping?.contentType || "",
                            };
                            form.setFieldsValue({ businessDocs: currentMappings });
                          }}
                        >
                          {availableBusinessDocs.map((doc) => (
                            <Option key={doc.docId} value={doc.docId}>
                              {doc.docName}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "contentType"]} hidden>
                        <Input />
                      </Form.Item>
                      <Space>
                        {selectedDoc?.cdnUrl && (
                          <Button href={selectedDoc.cdnUrl} target="_blank" rel="noopener noreferrer">
                            {BUSINESS_DOCS_LABELS.VIEW_DOC}
                          </Button>
                        )}
                        <Button danger onClick={() => remove(name)}>
                          {BUSINESS_DOCS_LABELS.DELINK_DOC}
                        </Button>
                      </Space>
                    </div>
                  );
                })}
                <Button
                  className={styles.bottomBarBtnOutline}
                  icon={<PlusOutlined />}
                  disabled={watchedBusinessDocs.length >= BUSINESS_DOCS_CONFIG.MAX_DOCS_PER_BIZ}
                  onClick={() => add({ label: "", docId: undefined })}
                >
                  {BUSINESS_DOCS_LABELS.ADD_MAPPING}
                </Button>
              </>
            )}
          </Form.List>
        </div>

      </div>{/* end settingsGroup — Storefront */}

      </div>{/* end settingsGrid */}

      <div style={{ height: 44 }} />
    </>
  );

  const isCatalogEmpty = currentProductsCount === 0 && currentServicesCount === 0;

  const normalizeCatalogItemsForSave = React.useCallback((items = []) =>
    items.map((item) => {
      const { _pendingImages, ...rest } = item || {};
      void _pendingImages;
      const images = Array.isArray(rest[HUB_ITEM.ItemImages])
        ? rest[HUB_ITEM.ItemImages]
            .map((img) => {
              if (typeof img === "string") {
                const imageKey = basenameFromStorageKey(img) || img;
                return imageKey ? { imageKey } : null;
              }
              if (!img?.imageKey) return null;
              return {
                ...img,
                imageKey: basenameFromStorageKey(img.imageKey) || img.imageKey,
              };
            })
            .filter(Boolean)
        : [];
      return {
        ...rest,
        [HUB_ITEM.ItemImages]: images,
      };
    }), []);

  const handleBuilderSave = React.useCallback(async (productData) => {
    setBuilderSaving(true);
    try {
      const isItemService = activeItemsTab === "services";
      const tab = isItemService ? "services" : "products";
      const currentItems = tab === "products" ? catalogProductsRef.current : catalogServicesRef.current;
      const existingIdx = editingIdxRef.current;
      const selectedLimit = isItemService ? serviceItemLimit : productItemLimit;
      if (existingIdx == null && currentItems.length >= selectedLimit) {
        message.error(
          isItemService
            ? CATALOG_LIMIT_LABELS.serviceLimit(selectedLimit)
            : CATALOG_LIMIT_LABELS.productLimit(selectedLimit)
        );
        return false;
      }
      const itemId = productData.ItemId || GenerateShortID(6);
      const pendingFiles = productData._pendingImages || [];
      const keptExistingImages = productData._existingImages || [];
      delete productData._pendingImages;
      delete productData._existingImages;

      const effectiveBizId = createdBizId || initialValues?.bizId;
      const effectiveHubId = createdHubId || selectedHubProfile?.PK;

      let uploadedImages = [];
      if (effectiveBizId && effectiveHubId && pendingFiles.length > 0) {
        try {
          const imagesMeta = pendingFiles.map((file) => ({
            fileName: file.name,
            sizeBytes: file.size,
          }));
          const manifestTags = [isItemService ? "service" : "product"];
          const itemData = {
            hubId: effectiveHubId,
            storeId: cdnSafeMediaPathSegment(effectiveBizId),
            itemId: ITEMS_SHARED_GALLERY_ITEM_ID,
            images: imagesMeta,
            gallerySegment: "items",
            manifestTags,
            operationType: OPERATION_TYPE.PUT_ITEMS_GALLERY_PRESIGNED_URL,
          };
          const response = await axios.post(handleMedia, Encrypt({ token, action: "uploadImages", itemData }), { headers: { "Content-Type": "application/json" } });
          const result = response.data;
          if (result.success) {
            const imageToUpload = result.uploadedImages || [];
            if (imageToUpload.length === pendingFiles.length) {
              for (let i = 0; i < imageToUpload.length; i++) {
                const { presignedUrl, contentType } = imageToUpload[i];
                const file = pendingFiles[i];
                await putPresignedObject(presignedUrl, file, contentType || file.type);
              }
              uploadedImages = imageToUpload.map((img, index) => ({
                imageKey: basenameFromStorageKey(img.imageKey) || img.imageKey,
                originalName: img.originalName || pendingFiles[index]?.name || null,
              }));
              if (result.manifestPresignedUrl) {
                try {
                  const prefix = mediaPath({ entity: "item", fileType: "media", hubId: effectiveHubId, bizId: effectiveBizId });
                  const existingManifest = await fetchManifestForPath(prefix, { token, preferOrigin: true });
                  const existingEntries = [...(existingManifest?.images || [])];
                  const seenKeys = new Set(existingEntries.map((e) => e.imageKey));
                  const collectCurrentItemImages = (items, tag) => {
                    for (const item of items) {
                      const imgs = Array.isArray(item[HUB_ITEM.ItemImages]) ? item[HUB_ITEM.ItemImages] : [];
                      for (const img of imgs) {
                        const basename = basenameFromStorageKey(img.imageKey) || img.imageKey;
                        if (!seenKeys.has(basename)) {
                          seenKeys.add(basename);
                          existingEntries.push({ imageKey: basename, originalFilename: img.originalName || img.imageKey, tags: [tag] });
                        }
                      }
                    }
                  };
                  const allProducts = catalogProductsRef.current;
                  const allServices = catalogServicesRef.current;
                  if (tab === "products") collectCurrentItemImages(allProducts, "product");
                  else collectCurrentItemImages(allServices, "service");
                  for (let index = 0; index < imageToUpload.length; index += 1) {
                    const img = imageToUpload[index];
                    const basename = basenameFromStorageKey(img.imageKey) || img.imageKey;
                    if (!seenKeys.has(basename)) {
                      seenKeys.add(basename);
                      existingEntries.push({
                        imageKey: basename,
                        originalFilename: img.originalName || pendingFiles[index]?.name || img.imageKey,
                        tags: [...manifestTags],
                        linkedItemIds: [itemId],
                      });
                    }
                  }
                  const nextManifest = { ...existingManifest, version: (existingManifest?.version || 1) + 1, images: existingEntries };
                  await putManifestWithPresignedUrl(result.manifestPresignedUrl, nextManifest);
                  invalidateManifestFetchCacheForPath(prefix);
                  dispatch(setGalleryManifestForPath({ pathPrefix: prefix, manifest: nextManifest, bumpImageCache: true }));
                } catch (mErr) { console.warn("Manifest write", mErr); message.warning("Image uploaded, but gallery list update failed. Use Refresh media if it does not appear."); }
              } else {
                message.warning("Image uploaded, but no gallery list upload URL was returned.");
              }
            }
          }
        } catch (e) {
          console.error("Image upload failed", e);
          const detail = e?.message && !/failed to fetch/i.test(e.message) ? ` (${e.message})` : "";
          message.warning(`Image upload failed, item saved without images${detail}`);
        }
      }

      const baseItem = {
        [HUB_ITEM.ItemName]: productData.ItemName || "",
        [HUB_ITEM.Description]: productData.Description || "",
        [HUB_ITEM.BasePrice]: productData.BasePrice ?? 0,
        [HUB_ITEM.Discount]: productData.Discount ?? 0,
        [HUB_ITEM.Variants]: productData.variants || [],
        [HUB_ITEM.Customisations]: productData.customisations || [],
        [HUB_ITEM.ItemId]: itemId,
        [HUB_ITEM.ItemType]: isItemService ? "SERVICE" : "PRODUCT",
        [HUB_ITEM.RequiresPrebooking]: productData[HUB_ITEM.RequiresPrebooking] === true,
        [HUB_ITEM.ItemAvailable]: productData[HUB_ITEM.ItemAvailable] !== false,
        [HUB_ITEM.VideoUrls]: productData.VideoUrls ?? [],
      };

      const mergedImages = [...keptExistingImages, ...uploadedImages];
      const nextItemData = { ...baseItem, [HUB_ITEM.ItemImages]: mergedImages };
      if (existingIdx != null) {
        const updated = [...currentItems];
        updated[existingIdx] = { ...updated[existingIdx], ...nextItemData };
        form.setFieldsValue({ [tab]: updated });
        if (tab === "products") catalogProductsRef.current = updated;
        else catalogServicesRef.current = updated;
        dispatch(tab === "products" ? setCatalogProducts(updated) : setCatalogServices(updated));
        editingIdxRef.current = existingIdx;
        setEditingProductIdx(existingIdx);
        setEditingProductData(updated[existingIdx]);
      } else {
        const updated = [...currentItems, nextItemData];
        const nextIndex = updated.length - 1;
        form.setFieldsValue({ [tab]: updated });
        if (tab === "products") catalogProductsRef.current = updated;
        else catalogServicesRef.current = updated;
        dispatch(tab === "products" ? setCatalogProducts(updated) : setCatalogServices(updated));
        editingIdxRef.current = nextIndex;
        setEditingProductIdx(nextIndex);
        setEditingProductData(updated[nextIndex]);
      }

      setItemsVersion(v => v + 1);
      setBuilderOpen(true);
      if (effectiveBizId && effectiveHubId) {
        try {
          const values = form.getFieldsValue(true);
          const updateShop = { bizId: effectiveBizId, hubId: effectiveHubId, version: 1, products: normalizeCatalogItemsForSave(catalogProductsRef.current), services: normalizeCatalogItemsForSave(catalogServicesRef.current), name: values.name, storeType: Array.isArray(values.storeType) ? values.storeType.sort().join(",") : values.storeType, address: values.address, city: values.city, country: values.country, zip: values.zip, phone: values.phone, productSegment: values.productSegment || "", serviceSegment: values.serviceSegment || "", activeFlag: "active", BizSettings: {}, extras: buildTypesenseExtras(values), updateTarget: "ITEMS",domain: selectedHubProfile?.Domain ?? "" };
          const syncRes = await handleAddOrEditShop({ shop: updateShop, mode: "edit", token });
          if (syncRes) {
            // ITEMS updates may not return version — trust the standard paths
            const newVer = syncRes?.data?.Items?.[0]?.version ?? syncRes?.data?.version ?? syncRes?.data?.Attributes?.version ?? syncRes?.version;
            if (newVer) bizVersionRef.current = newVer;
          }
          try {
            await syncItemsGalleryLinkedItemIds({
              token,
              hubId: effectiveHubId,
              bizId: effectiveBizId,
              products: catalogProductsRef.current,
              services: catalogServicesRef.current,
              bizPrivate: !!values.bizPrivate,
              manifestCrypto: null,
            });
          } catch (syncError) {
            console.warn("syncItemsGalleryLinkedItemIds", syncError);
          }
        } catch (e) { console.error("Item sync failed", e); message.warning("Item saved locally but sync failed"); }
      }
      if (bizEditBaseline) {
        dispatch(setBizEditBaseline({
          ...bizEditBaseline,
          productsKey: sortedItemsComparableKey(catalogProductsRef.current),
          servicesKey: sortedItemsComparableKey(catalogServicesRef.current),
          itemsGalleryLinkageKey: itemsGalleryLinkageComparableKey(
            catalogProductsRef.current,
            catalogServicesRef.current
          ),
          baselineProducts: catalogProductsRef.current,
          baselineServices: catalogServicesRef.current,
        }));
      }
      dispatch(setCatalogBuilderDirty(true));
      return nextItemData;
    } finally {
      setBuilderSaving(false);
    }
  }, [activeItemsTab, form, createdBizId, createdHubId, initialValues, selectedHubProfile, token, message, dispatch, bizEditBaseline, normalizeCatalogItemsForSave, productItemLimit, serviceItemLimit, buildTypesenseExtras]);

  const handleCatalogImport = React.useCallback((importedItems) => {
    if (!importedItems || importedItems.length === 0) return;

    const productItems = importedItems.filter((item) => item.ItemType !== "SERVICE");
    const serviceItems = importedItems.filter((item) => item.ItemType === "SERVICE");
    const productSlots = Math.max(0, productItemLimit - catalogProductsRef.current.length);
    const serviceSlots = Math.max(0, serviceItemLimit - catalogServicesRef.current.length);
    const acceptedProducts = productItems.slice(0, productSlots);
    const acceptedServices = serviceItems.slice(0, serviceSlots);

    if (acceptedProducts.length > 0 && visibleStoreType.includes("P")) {
      const updated = [...catalogProductsRef.current, ...acceptedProducts];
      catalogProductsRef.current = updated;
      form.setFieldsValue({ products: updated });
      dispatch(setCatalogProducts(updated));
    }
    if (acceptedServices.length > 0 && visibleStoreType.includes("S")) {
      const updated = [...catalogServicesRef.current, ...acceptedServices];
      catalogServicesRef.current = updated;
      form.setFieldsValue({ services: updated });
      dispatch(setCatalogServices(updated));
    }
    if (acceptedProducts.length < productItems.length) {
      message.warning(CATALOG_LIMIT_LABELS.importedProducts(productSlots));
    }
    if (acceptedServices.length < serviceItems.length) {
      message.warning(CATALOG_LIMIT_LABELS.importedServices(serviceSlots));
    }

    dispatch(setCatalogBuilderDirty(true));
    setItemsVersion(v => v + 1);
    setImportOpen(false);
  }, [
    dispatch,
    form,
    message,
    productItemLimit,
    serviceItemLimit,
    visibleStoreType,
  ]);

  const effectiveHubId = createdHubId || selectedHubProfile?.PK;
  const effectiveBizId = createdBizId || initialValues?.bizId;
  const effectiveDomain = selectedHubProfile?.Domain ?? "";

  const handleCatalogSave = React.useCallback(async () => {
    if (loading || builderSaving) return;
    if (!catalogWithinPlanLimits()) return;
    setBuilderSaving(true);
    try {
      if (effectiveBizId && effectiveHubId) {
        form.setFieldsValue({ products: catalogProductsRef.current, services: catalogServicesRef.current });
        const values = form.getFieldsValue(true);
        const canRemainLive = hasCatalogItems();
        if (values.bizLive === true && !canRemainLive) {
          form.setFieldsValue({ bizLive: false });
          message.warning("The store was kept as Draft because the catalogue has no products or services.");
        }
        const updateShop = {
          bizId: effectiveBizId,
          hubId: effectiveHubId,
          domain: effectiveDomain,
          version: bizVersionRef.current || 1,
          products: normalizeCatalogItemsForSave(catalogProductsRef.current),
          services: normalizeCatalogItemsForSave(catalogServicesRef.current),
          name: values.name,
          storeType: Array.isArray(values.storeType) ? values.storeType.sort().join(",") : values.storeType,
          address: values.address,
          city: values.city,
          country: values.country,
          zip: values.zip,
          phone: values.phone,
          productSegment: values.productSegment || "",
          serviceSegment: values.serviceSegment || "",
          activeFlag: "active",
          BizSettings: {},
          extras: { ...buildTypesenseExtras(values), bizLive: values.bizLive === true && canRemainLive },
          updateTarget: "ITEMS"
        };
        const syncRes = await handleAddOrEditShop({ shop: updateShop, mode: "edit", token });
        if (syncRes) {
          const newVer = syncRes?.data?.Items?.[0]?.version ?? syncRes?.data?.version ?? syncRes?.data?.Attributes?.version ?? syncRes?.version;
          if (newVer) bizVersionRef.current = newVer;
        }
        try {
          await syncItemsGalleryLinkedItemIds({
            token,
            hubId: effectiveHubId,
            bizId: effectiveBizId,
            products: catalogProductsRef.current,
            services: catalogServicesRef.current,
            bizPrivate: !!values.bizPrivate,
            manifestCrypto: null,
          });
        } catch (syncError) {
          console.warn("syncItemsGalleryLinkedItemIds", syncError);
        }
      }
      if (bizEditBaseline) {
        dispatch(setBizEditBaseline({
          ...bizEditBaseline,
          productsKey: sortedItemsComparableKey(catalogProductsRef.current),
          servicesKey: sortedItemsComparableKey(catalogServicesRef.current),
          itemsGalleryLinkageKey: itemsGalleryLinkageComparableKey(
            catalogProductsRef.current,
            catalogServicesRef.current
          ),
          baselineProducts: catalogProductsRef.current,
          baselineServices: catalogServicesRef.current,
        }));
      }
      dispatch(setCatalogBuilderDirty(false));
      message.success("Catalog saved successfully");
    } catch (e) {
      console.error("Catalog save failed", e);
      message.error("Catalog save failed");
    } finally {
      setBuilderSaving(false);
    }
  }, [loading, builderSaving, catalogWithinPlanLimits, effectiveBizId, effectiveHubId, effectiveDomain, form, token, dispatch, message, bizEditBaseline, normalizeCatalogItemsForSave, buildTypesenseExtras]);

  const buildCatalogDraftItem = React.useCallback((draft, tab, currentItem = {}) => {
    const isItemService = tab === "services";
    const variants = draft.variants ?? draft[HUB_ITEM.Variants] ?? currentItem[HUB_ITEM.Variants] ?? [];
    const customisations = draft.customisations ?? draft[HUB_ITEM.Customisations] ?? currentItem[HUB_ITEM.Customisations] ?? [];
    const existingImages = draft._existingImages ?? draft[HUB_ITEM.ItemImages] ?? currentItem[HUB_ITEM.ItemImages] ?? [];
    const pendingImages = draft._pendingImages ?? currentItem._pendingImages ?? [];
    const nextItem = {
      ...currentItem,
      [HUB_ITEM.ItemId]: draft[HUB_ITEM.ItemId] || currentItem[HUB_ITEM.ItemId] || GenerateShortID(6),
      [HUB_ITEM.ItemType]: isItemService ? "SERVICE" : "PRODUCT",
      [HUB_ITEM.ItemName]: draft[HUB_ITEM.ItemName] ?? currentItem[HUB_ITEM.ItemName] ?? "",
      [HUB_ITEM.Description]: draft[HUB_ITEM.Description] ?? currentItem[HUB_ITEM.Description] ?? "",
      [HUB_ITEM.BasePrice]: draft[HUB_ITEM.BasePrice] ?? currentItem[HUB_ITEM.BasePrice] ?? 0,
      [HUB_ITEM.Discount]: draft[HUB_ITEM.Discount] ?? currentItem[HUB_ITEM.Discount] ?? 0,
      [HUB_ITEM.Variants]: Array.isArray(variants) ? variants : [],
      [HUB_ITEM.Customisations]: Array.isArray(customisations) ? customisations : [],
      [HUB_ITEM.ItemImages]: Array.isArray(existingImages) ? existingImages : [],
      [HUB_ITEM.RequiresPrebooking]: draft[HUB_ITEM.RequiresPrebooking] ?? currentItem[HUB_ITEM.RequiresPrebooking] ?? false,
      [HUB_ITEM.ItemAvailable]: draft[HUB_ITEM.ItemAvailable] ?? currentItem[HUB_ITEM.ItemAvailable] ?? true,
      [HUB_ITEM.VideoUrls]: draft[HUB_ITEM.VideoUrls] ?? currentItem[HUB_ITEM.VideoUrls] ?? [],
      _pendingImages: Array.isArray(pendingImages) ? pendingImages : [],
    };

    return nextItem;
  }, []);

  const handleCatalogDraftChange = React.useCallback((draft) => {
    if (!builderOpen || !draft) return;
    const tab = activeItemsTab;
    const currentItems = tab === "products" ? catalogProductsRef.current : catalogServicesRef.current;
    const existingIdx = editingIdxRef.current;
    const targetIndex = existingIdx == null ? currentItems.length : existingIdx;
    const updated = [...currentItems];
    updated[targetIndex] = buildCatalogDraftItem(draft, tab, updated[targetIndex]);

    editingIdxRef.current = targetIndex;
    setEditingProductIdx(targetIndex);
    form.setFieldsValue({ [tab]: updated });
    if (tab === "products") {
      catalogProductsRef.current = updated;
    } else {
      catalogServicesRef.current = updated;
    }
    dispatch(tab === "products" ? setCatalogProducts(updated) : setCatalogServices(updated));
    dispatch(setCatalogBuilderDirty(true));
  }, [activeItemsTab, buildCatalogDraftItem, builderOpen, dispatch, form]);

  const resolveItemImageUrl = (img) => {
    if (!img?.imageKey) return null;
    const key = String(img.imageKey);
    if (!key.includes("/") && effectiveHubId && effectiveBizId) {
      return cdnUrl({ entity: "item", fileType: "media", hubId: effectiveHubId, bizId: effectiveBizId, fileName: basenameFromStorageKey(key) || key });
    }
    return cdnUrlForStorageKey(key);
  };

  const renderStep3 = () => {
    const productsList = catalogProducts;
    const servicesList = catalogServices;
    const isStoreLive = !!watchedValues?.bizLive;
    const normalizedCatalogSearch = catalogSearchTerm.trim().toLowerCase();
    const matchesCatalogSearch = (item) => {
      if (!normalizedCatalogSearch) return true;
      const itemName = String(item?.[HUB_ITEM.ItemName] || "").toLowerCase();
      const itemDescription = String(item?.[HUB_ITEM.Description] || "").toLowerCase();
      return itemName.includes(normalizedCatalogSearch) || itemDescription.includes(normalizedCatalogSearch);
    };
    const filteredProductsList = productsList.filter(matchesCatalogSearch);
    const filteredServicesList = servicesList.filter(matchesCatalogSearch);

     const openEditor = (type, index) => {
       if (index == null && type === "products" && productLimitReached) {
         message.warning(CATALOG_LIMIT_LABELS.productLimit(productItemLimit));
         return;
       }
       if (index == null && type === "services" && serviceLimitReached) {
         message.warning(CATALOG_LIMIT_LABELS.serviceLimit(serviceItemLimit));
         return;
       }
       const switchingItem =
         builderOpen &&
         (activeItemsTab !== type || editingIdxRef.current !== index);
       if (switchingItem) {
         if (productBuilderRef.current?.hasUnsavedNestedDraft?.()) {
           message.warning("Save or cancel the current variant/customisation before switching items.");
           return;
         }
         const currentDraft = productBuilderRef.current?.getItemData();
         if (currentDraft?._pendingImages?.length) {
           message.warning("Save the current item images before switching items.");
           return;
         }
       }
        setActiveItemsTab(type);
        if (index != null) {
          const items = type === "products" ? catalogProductsRef.current : catalogServicesRef.current;
          setEditingProductData({ ...(items[index] || {}) });
          editingIdxRef.current = index;
          setEditingProductIdx(index);
        } else {
          setEditingProductData(null);
          editingIdxRef.current = null;
          setEditingProductIdx(null);
          setProductBuilderKey(k => k + 1);
        }
        setBuilderOpen(true);
     };

    const deleteItem = (e, index, type, item) => {
      e.stopPropagation();
      const currentItems = type === "products" ? catalogProductsRef.current : catalogServicesRef.current;
      const updated = currentItems.filter((_, i) => i !== index);
      form.setFieldsValue({ [type]: updated });
      if (type === "products") {
        catalogProductsRef.current = updated;
        dispatch(setCatalogProducts(updated));
      } else {
        catalogServicesRef.current = updated;
        dispatch(setCatalogServices(updated));
      }
      dispatch(setCatalogBuilderDirty(true));
      const itemTypeKey = type === "services" ? "SERVICE" : "PRODUCT";
      handleItemRemove(item[HUB_ITEM.ItemId], itemTypeKey);
      if (builderOpen && editingProductIdx === index && activeItemsTab === type) {
        setBuilderOpen(false);
        editingIdxRef.current = null;
        setEditingProductIdx(null);
        setEditingProductData(null);
      }
      setItemsVersion(v => v + 1);
    };

    const renderSidebarItem = (item, index, type) => {
      const isEditing = builderOpen && editingProductIdx === index && activeItemsTab === type;
      const name = item[HUB_ITEM.ItemName] || "Untitled";
      const basePrice = item[HUB_ITEM.BasePrice];
      const price = basePrice != null && basePrice !== "" ? `₹${Number(basePrice).toLocaleString()}` : "—";
      const images = Array.isArray(item[HUB_ITEM.ItemImages]) ? item[HUB_ITEM.ItemImages] : [];
      const primaryImgKey = resolveItemImageUrl(images[0]);
      return (
        <div
          key={item[HUB_ITEM.ItemId] || `${type}-${index}`}
          className={`${styles.sidebarItem}${isEditing ? ` ${styles.sidebarItemActive}` : ""}`}
          onClick={() => openEditor(type, index)}
          data-testid={`catalog-item-${index}`}
        >
          <div className={styles.sidebarItemThumb}>
            {primaryImgKey
              ? <img src={primaryImgKey} alt="" className={styles.sidebarItemThumbImg} />
              : <span className={styles.sidebarItemThumbPlaceholder}>{type === "services" ? "S" : "P"}</span>
            }
          </div>
          <div className={styles.sidebarItemInfo}>
            <div className={styles.sidebarItemName} data-testid={`catalog-item-name-${index}`}>{name}</div>
            <div className={styles.sidebarItemMeta}>
              <span className={styles.sidebarItemPrice} data-testid={`catalog-item-price-${index}`}>{price}</span>
              {Array.isArray(item[HUB_ITEM.VideoUrls]) && item[HUB_ITEM.VideoUrls].filter(Boolean).length > 0 && (
                <span className={styles.sidebarVideoBadge}>▶ {item[HUB_ITEM.VideoUrls].filter(Boolean).length}</span>
              )}
              <span className={isStoreLive ? styles.sidebarBadgeLive : styles.sidebarBadgeDraft}>
                {isStoreLive ? "Live" : "Draft"}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={styles.sidebarItemDel}
            onClick={e => deleteItem(e, index, type, item)}
            title="Delete"
          >
            {"✕"}
          </button>
        </div>
      );

    };

    const showProducts = visibleStoreType.includes("P") || visibleStoreType.length === 0;
    const showServices = visibleStoreType.includes("S") || visibleStoreType.length === 0;

    return (
      <>
        <div className={styles.pageHeader}>{BIZ.PAGE_TITLE}</div>
        {renderProgressBar()}

        {/* Template banner — shown when step 3 was auto-populated from a product or service template */}
        {templateApplied && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 16px", marginBottom: 12, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>{"✨"}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#92400e" }}>
                  {`Sample ${templateApplied.type} catalog generated from template`}
                </div>
                <div style={{ fontSize: 12, color: "#b45309", marginTop: 1 }}>
                  {`Based on your "${templateApplied.category}" category — edit any ${templateApplied.type} or add your own.`}
                </div>
              </div>
            </div>
            <Button
              size="small"
              onClick={() => {
                if (templateApplied.type === "service") {
                  catalogServicesRef.current = [];
                  form.setFieldsValue({ services: [] });
                  dispatch(setCatalogServices([]));
                } else {
                  catalogProductsRef.current = [];
                  form.setFieldsValue({ products: [] });
                  dispatch(setCatalogProducts([]));
                }
                dispatch(setCatalogBuilderDirty(true));
                setTemplateApplied(null);
                setBuilderOpen(false);
                setEditingProductIdx(null);
                setEditingProductData(null);
                editingIdxRef.current = null;
              }}
              style={{ flexShrink: 0, fontSize: 12 }}
            >
              Start from scratch
            </Button>
          </div>
        )}

        <div className={styles.catalogWorkspace}>
          {/* LEFT SIDEBAR */}
          <div className={styles.catalogSidebar} data-testid="catalog-sidebar">
            <div className={styles.sidebarHeader}>
              <span className={styles.sidebarTitle}>Catalog</span>
              <div className={styles.sidebarAddBtns}>
                {showProducts && (
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openEditor("products", null)}
                    disabled={productLimitReached}
                    title={productLimitReached ? CATALOG_LIMIT_LABELS.productButtonTitle(productItemLimit) : undefined}
                    data-testid="add-product-btn"
                  >
                    Product
                  </Button>
                )}
                {showServices && (
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openEditor("services", null)}
                    disabled={serviceLimitReached}
                    title={serviceLimitReached ? CATALOG_LIMIT_LABELS.serviceButtonTitle(serviceItemLimit) : undefined}
                    data-testid="add-service-btn"
                  >
                    Service
                  </Button>
                )}
              </div>
            </div>

            <div className={styles.sidebarSearch}>
              <span className={styles.sidebarSearchIcon}>{"🔍"}</span>
              <input
                type="text"
                value={catalogSearchTerm}
                onChange={(event) => setCatalogSearchTerm(event.target.value)}
                placeholder="Search items..."
                className={styles.sidebarSearchInput}
              />
            </div>

            <div className={styles.sidebarList}>
              {showProducts && (
                <>
                  <div className={styles.sidebarSection}>
                    <span className={styles.sidebarSectionLabel}>Products</span>
                    <span className={styles.sidebarSectionCount}>{filteredProductsList.length}</span>
                  </div>
                  {filteredProductsList.length > 0
                    ? filteredProductsList.map((item) => {
                        const idx = productsList.findIndex((product) => product?.[HUB_ITEM.ItemId] === item?.[HUB_ITEM.ItemId]);
                        return renderSidebarItem(item, idx, "products");
                      })
                    : <div className={styles.sidebarEmptySection}>{normalizedCatalogSearch ? "No matching products" : "No products yet"}</div>
                  }
                </>
              )}
              {showServices && (
                <>
                  <div className={styles.sidebarSection} style={{ marginTop: 10 }}>
                    <span className={styles.sidebarSectionLabel}>Services</span>
                    <span className={styles.sidebarSectionCount}>{filteredServicesList.length}</span>
                  </div>
                  {filteredServicesList.length > 0
                    ? filteredServicesList.map((item) => {
                        const idx = servicesList.findIndex((service) => service?.[HUB_ITEM.ItemId] === item?.[HUB_ITEM.ItemId]);
                        return renderSidebarItem(item, idx, "services");
                      })
                    : <div className={styles.sidebarEmptySection}>{normalizedCatalogSearch ? "No matching services" : "No services yet"}</div>
                  }
                </>
              )}
            </div>

          </div>

          {/* RIGHT EDITOR PANEL */}
          <div className={styles.catalogEditorPanel} data-testid="catalog-editor-panel">
            {builderOpen ? (
              <ProductBuilder key={productBuilderKey}
                visible={builderOpen}
                inline={true}
                itemType={activeItemsTab === "services" ? "SERVICE" : "PRODUCT"}
                initialData={editingProductIdx != null ? editingProductData : null}
                onSave={handleBuilderSave}
                onCancel={() => {
                  if (builderSaving) return;
                  if (productBuilderRef.current?.hasUnsavedNestedDraft?.()) {
                    message.warning("Save or cancel the current variant/customisation before closing the editor.");
                    return;
                  }
                  const currentDraft = productBuilderRef.current?.getItemData();
                  if (currentDraft?._pendingImages?.length) {
                    message.warning("Save the current item images before closing the editor.");
                    return;
                  }
                  setBuilderOpen(false);
                  editingIdxRef.current = null;
                  setEditingProductIdx(null);
                  setEditingProductData(null);
                }}
                saving={builderSaving}
                token={token}
                hubId={createdHubId || selectedHubProfile?.PK}
                bizId={createdBizId || initialValues?.bizId}
                bizPrivate={!!watchedValues?.bizPrivate}
                storeType={Array.isArray(watchedValues?.storeType) ? watchedValues.storeType.join(",") : watchedValues?.storeType || ""}
                showSaveButton={false}
                prebookingDisabled={prebookingGroups.length === 0}
                onDirtyChange={handleCatalogDirtyChange}
                onDraftChange={handleCatalogDraftChange}
                ref={productBuilderRef}
              />
            ) : isCatalogEmpty ? (
              <div className={styles.catalogEditorEmpty}>
                <div className={styles.catalogIllustration}>
                  <span className={styles.catalogEmoji}>{"🛍"}</span>
                  <div className={styles.catalogBadge}>{"＋"}</div>
                </div>
                <div className={styles.catalogHeadline}>{BIZ.CATALOG_HEADLINE}</div>
                <div className={styles.catalogBody}>{BIZ.CATALOG_BODY}</div>
                <ActionGroup
                  align="start"
                  actions={[
                    {
                      key: "import-excel",
                      label: BIZ.CATALOG_IMPORT,
                      icon: <UploadOutlined />,
                      variant: "secondary",
                      onClick: () => setImportOpen(true),
                    },
                    {
                      key: "add-product",
                      label: BIZ.CATALOG_ADD_PRODUCT,
                      icon: <PlusOutlined />,
                      variant: "primary",
                      onClick: () => openEditor("products", null),
                      condition: showProducts,
                    },
                    {
                      key: "add-service",
                      label: BIZ.CATALOG_ADD_SERVICE,
                      icon: <PlusOutlined />,
                      variant: "primary",
                      onClick: () => openEditor("services", null),
                      condition: showServices,
                    },
                    {
                      key: "generate-template",
                      label: "Generate from template",
                      icon: <span>{"✨"}</span>,
                      variant: "amber",
                      onClick: () => {
                        const isProd = showProducts && hasProductTemplate(watchedProductSegment);
                        if (isProd) {
                          const tpl = getProductTemplate(watchedProductSegment);
                          form.setFieldsValue({ products: tpl.suggestedProducts });
                          catalogProductsRef.current = tpl.suggestedProducts;
                          dispatch(setCatalogProducts(tpl.suggestedProducts));
                          setTemplateApplied({ category: watchedProductSegment, type: "product" });
                        } else {
                          const tpl = getServiceTemplate(watchedServiceSegment);
                          form.setFieldsValue({ services: tpl.suggestedServices });
                          catalogServicesRef.current = tpl.suggestedServices;
                          dispatch(setCatalogServices(tpl.suggestedServices));
                          setTemplateApplied({ category: watchedServiceSegment, type: "service" });
                        }
                      },
                      condition: (showProducts && hasProductTemplate(watchedProductSegment)) || (showServices && hasServiceTemplate(watchedServiceSegment)),
                    },
                  ]}
                />
              </div>
            ) : (
              <div className={styles.catalogEditorEmpty}>
                <div className={styles.catalogIllustration}>
                  <span className={styles.catalogEmoji}>{"🛍"}</span>
                  <div className={styles.catalogBadge}>{"＋"}</div>
                </div>
                <div className={styles.catalogEditorEmptyTitle}>Select an item to edit</div>
                <div className={styles.catalogEditorEmptyBody}>
                  Click any item in the sidebar to edit it.
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const stepSubmitLabel = step === TOT_STEPS
    ? (loading ? "Saving..." : BIZ.FINISH_SETUP)
    : step === 1 ? "Continue"
    : mode === "edit" ? "Update Biz"
    : BIZ.SAVE_CONTINUE;

  return (
    <div className={styles.container}>
      <Breadcrumb items={[
        { label: 'My Hub', path: '/hub' },
        { label: 'Create Store' },
        { label: `Step ${step}` }
      ]} />
      {mode === "edit" && watchedValues?.bizPrivate && !privateEditUnlocked && getBizPasskeyHashFromSettings(initialValues?.BizSettings) && (
        <div className={styles.privateBizBanner} style={{ marginBottom: 16, padding: 12, background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6 }}>
          This private Biz is locked. Enter your current passkey to enable editing and saving changes.{" "}
          <Button type="primary" size="small" onClick={() => setEditUnlockModalOpen(true)}>Unlock</Button>
        </div>
      )}
      <div ref={formRef}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={handleFinishFailed}
        onValuesChange={() => { formDirtyRef.current = true; }}
        autoComplete="off"
        initialValues={{
          bizImageKey: "", phonePrefix: "+91", phoneDigits: "", phone: "", storeType: [],
          geocode: mode === "edit" ? geocodeFormValue(initialValues?.geocode) : "",
        }}
        disabled={mode === "edit" && !!watchedValues?.bizPrivate && !privateEditUnlocked}
      >
        <Form.Item name="storeType" hidden rules={[{ required: true, message: BIZ.TYPE_REQUIRED }]}>
          <Input />
        </Form.Item>

        <div style={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 200px)" }}>
          <div className={styles.wizardContent}>
            <div style={{ display: step === 1 ? "" : "none" }}>{renderStep1()}</div>
            <div style={{ display: step === 2 ? "" : "none" }}>{renderStep2()}</div>
            <div style={{ display: step === 3 ? "" : "none" }}>{renderStep3()}</div>
          </div>
          <div className={styles.bottomBar}>
            <div style={{ display: "flex", gap: 6 }}>
              {(() => {
                const isStep3 = step === TOT_STEPS;
                const wCancel = step === 1 ? "30%" : isStep3 ? "20%" : "25%";
                const wPreview = step === 1 ? "30%" : isStep3 ? "25%" : "35%";
                const wSave = "20%";
                const wPrimary = isStep3 ? "35%" : "40%";
                return (
                  <>
                    {step > 1 ? (
                      <div className={styles.bottomBarBtnOutline} onClick={loading ? undefined : handleGoBack} style={{ width: wCancel }}>{BIZ.BACK}</div>
                    ) : (
                      <div className={styles.bottomBarBtnOutline} onClick={loading ? undefined : handleCancelClick} style={{ width: wCancel }}>{BIZ.CANCEL}</div>
                    )}
                    <div className={styles.bottomBarBtnOutline} onClick={() => setPreviewOpen(true)} style={{ width: wPreview }} data-testid="preview-btn">
                      {BIZ.PREVIEW}
                    </div>
                    {isStep3 && (
                      <Tooltip title={catalogHasUnsavedChanges ? "You have unsaved changes" : ""}>
                        <div
                          className={styles.bottomBarBtnOutline}
                          onClick={
                            (!builderOpen && !catalogHasUnsavedChanges) || builderSaving || loading
                              ? undefined
                              : handleStep3SaveClick
                          }
                          style={{
                            width: wSave,
                            opacity: ((!builderOpen && !catalogHasUnsavedChanges) || builderSaving || loading) ? 0.5 : 1,
                            cursor: ((!builderOpen && !catalogHasUnsavedChanges) || builderSaving || loading) ? "not-allowed" : "pointer",
                            borderColor: catalogHasUnsavedChanges ? "#fa8c16" : undefined,
                            background: catalogHasUnsavedChanges ? "#fff7e6" : undefined,
                            color: catalogHasUnsavedChanges ? "#ad4e00" : undefined,
                            boxShadow: catalogHasUnsavedChanges ? "0 0 0 1px rgba(250, 140, 22, 0.2) inset" : undefined,
                          }}
                          data-testid="catalog-save-btn"
                        >
                          {builderSaving ? "Saving..." : (
                            <>
                              {catalogHasUnsavedChanges && (
                                <span style={{ display: "inline-flex", width: 8, height: 8, borderRadius: "50%", background: "#fa8c16", marginRight: 6 }} aria-hidden="true" />
                              )}
                              {BIZ.FOOTER_SAVE}
                            </>
                          )}
                        </div>
                      </Tooltip>
                    )}
                    <Button size="middle" type="primary"
                      htmlType={step === TOT_STEPS ? "button" : "button"}
                      loading={step === TOT_STEPS ? loading : step2Loading}
                      disabled={step === TOT_STEPS ? false : (loading || step2Loading)}
                      className={styles.bottomBarBtnPrimary} data-testid="submit-biz-btn"
                      onClick={step === TOT_STEPS ? handleFinishSetupClick : handleGoForward}
                      style={{ width: wPrimary }}
                    >
                      {stepSubmitLabel}
                    </Button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </Form>
      </div>

      {/* Preview overlay — StorefrontPreview component */}
      {previewOpen && (
        <div className={styles.previewOverlay} onClick={() => setPreviewOpen(false)} data-testid="preview-overlay">
          <div className={styles.previewFullCard} onClick={e => e.stopPropagation()} data-testid="preview-modal">
            <div className={styles.previewCustHeader}>
              <span className={styles.previewCustHeaderLogo}>Store Preview</span>
              <div className={styles.previewCustHeaderRight}>
                <Button type="text" size="small" style={{ padding: "2px 4px", lineHeight: 1, color: "#888" }} onClick={() => setPreviewOpen(false)}>{"\u2715"}</Button>
              </div>
            </div>
            <div className={styles.previewScrollBody}>
              <StorefrontPreview
                watchedValues={watchedValues}
                products={catalogProducts}
                services={catalogServices}
                resolveImg={resolveItemImageUrl}
                hubId={effectiveHubId}
                bizId={effectiveBizId}
              />
            </div>
            <div className={styles.previewCustFooter}>
              <span className={styles.previewPoweredBy}>Powered by ziniz</span>
              <Button size="small" onClick={() => setPreviewOpen(false)}>{BIZ.PREVIEW_CLOSE}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Catalog import modal */}
      <CatalogImport
        visible={importOpen}
        onCancel={() => setImportOpen(false)}
        onImport={handleCatalogImport}
        existingItems={[
          ...catalogProducts,
          ...catalogServices,
        ]}
      />

      {/* Existing modals — passkey unlock, edit unlock */}
      <Modal title="Unlock private Biz" open={editUnlockModalOpen} onOk={handleEditUnlockConfirm}
        afterOpenChange={(open) => { if (open) requestAnimationFrame(() => editUnlockPasskeyInputRef.current?.focus?.()); }}
        onCancel={() => { setEditUnlockModalOpen(false); setEditUnlockPasskeyInput(""); }}
        okText="Unlock" destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>Enter the current passkey for this store.</p>
        <Input.Password ref={editUnlockPasskeyInputRef} autoComplete="off" autoFocus
          value={editUnlockPasskeyInput}
          onChange={(event) => setEditUnlockPasskeyInput(event.target.value)}
          onPressEnter={handleEditUnlockConfirm} placeholder="Passkey"
        />
      </Modal>
      <Modal title="Enter Passkey" open={passkeyPromptOpen} onOk={handlePasskeyPromptConfirm}
        afterOpenChange={(open) => { if (open) requestAnimationFrame(() => passkeyPromptInputRef.current?.focus?.()); }}
        onCancel={() => setPasskeyPromptOpen(false)} okText="Unlock" destroyOnClose
      >
        <Input.Password ref={passkeyPromptInputRef} autoFocus value={passkeyPromptValue}
          onChange={(e) => setPasskeyPromptValue(e.target.value)}
          onPressEnter={handlePasskeyPromptConfirm} placeholder="Enter passkey"
        />
      </Modal>

      <Modal
        title="Unsaved Changes"
        open={navigationConfirmOpen}
        onOk={() => { setNavigationConfirmOpen(false); pendingNavActionRef.current?.(); }}
        onCancel={() => { setNavigationConfirmOpen(false); pendingNavActionRef.current = null; }}
        okText="Leave"
        okButtonProps={{ danger: true }}
        cancelText="Stay"
        destroyOnClose
      >
        <p>Your changes will be lost if you leave this page.</p>
      </Modal>
    </div>
  );
};

export default CreateStoreWithPreview;
